import cx from 'classnames';
import SelectBucket from 'js/components/data_sources/add_ds_form/SelectBucket';
import { isNUIURLStartsWith } from 'js/lib/URLUtils';
import {
  assign,
  capitalize,
  clone,
  cloneDeep,
  compact,
  difference,
  filter,
  find,
  forEach,
  get,
  groupBy,
  has,
  head,
  indexOf,
  isEmpty,
  isInteger,
  isNil,
  isString,
  keys,
  map,
  mapKeys,
  mapValues,
  omit,
  pick,
  reduce,
  replace,
  size,
  split,
  values,
} from 'lodash';
import { NavigatorForEditedNexset } from 'new/components/NavigatorForEditedNexset';
import { ConnectionLoader } from 'new/elements/ConnectionLoader/ConnectionLoader';
import { FileUtil } from 'new/utils/FileUtil';
import React from 'react';
import { Redirect } from 'react-router-dom';
import RoutePrefixContext from '../../../new/contexts/RoutePrefixContext';
import Constants, { CONNECTORS_WITHOUT_PATH } from '../../Constants';
import {
  CONNECTION_CATEGORY_DATA_MAP,
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_KAFKA,
  CONNECTION_CATEGORY_NOSQL,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_SOAP,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT,
  CONNECTION_CATEGORY_VECTOR_DB,
} from '../../constants/connectionCategories';
import { FORM_TYPE_EDIT_DESTINATION } from '../../constants/formTypes';
import {
  ROUTE_DATA_SETS_SHARED_WITH_ME,
  ROUTE_DESTINATIONS,
  ROUTE_MY_DATA_SETS,
  ROUTE_PIPELINES,
} from '../../constants/pathnames';
import { CONNECTORS_RESOURCE_TYPES } from '../../constants/resourceTypes';
import { SUPPORT_KEY_SINK_FORM_COMMON } from '../../constants/supportPageKeys';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import JSONUtils from '../../lib/JSONUtils';
import { getConnectionOrVendorType, isDetectedDataset } from '../../lib/ResourceUtils';
import { isCapsuleCluster } from 'js/lib/PipelineUtils';
import { didVendorEndpointJustArrive, hasVendorEndpoint } from '../../lib/templatizedApiEditGuards';
import UIUtils from '../../lib/UIUtils';
import {
  classifySavedTopic,
  resolveSinkTopicFieldName,
  topicListContains,
} from './destination-content/KafkaSinkConfig/kafkaTopicResolution';
import {
  getEmptyRequiredFields,
  formatValidationErrorMessage,
  isValidDBColumnName,
} from '../../lib/ValidationUtils';
import AnalyticsUtils from '../../lib/AnalyticsUtils';
import { withSnackbar } from '../../utils/withSnackbar';
import DropboxService from '../../services/DropboxService';
import BoxService from '../../services/BoxService';
import AuthenticateDataSource from '../data_sources/add_ds_form/AuthenticateDataSource';
import SelectConnection from '../data_sources/add_ds_form/SelectConnection';
import Form from '../elements/forms/Form';
import FormPage from '../elements/forms/FormPage';
import ConfigureTemplatizedAPISinkContainer from './destination-content/ConfigureTemplatizedAPISinkContainer';
import DatabaseConfigurator from './destination-content/database/DatabaseConfigurator';
import NoSQLConfigurator from './destination-content/database/NoSQLConfigurator';
import EditDynamicDataMap from './destination-content/EditDynamicDataMap';
import ConfigureKafkaSinkContainer from './destination-content/KafkaSinkConfig/ConfigureKafkaSinkContainer';
import RestConfigure from './destination-content/RestConfigure';
import { UiMode } from 'new/constants/uiMode';
import { isUiModeCode, withUiModeCode, withUiModeDefault } from 'new/utils/uiMode';

import './DestinationSetup.scss';

const PIPELINE_FORM_PATH_PATTERN = new RegExp(`${ROUTE_PIPELINES}/\\d+/edit`);
const PIPELINE_PROJECTS_PATH_PATTERN = new RegExp(`(.*)/projects(.*)`);

// Connector categories a capsule-cluster flow is allowed to write to. Keep in sync
// with the source-side runtime-selection wiring in AddDataSourceForm — the supported
// source/destination pair is "anywhere to anywhere within this set". Database is
// intentionally excluded today; backend support is not yet in place.
const CAPSULE_FLOW_DESTINATION_CATEGORIES = [
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
];

class DestinationSetup extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    this.state = this._getInitialState();

    this._getInitialState = this._getInitialState.bind(this);
    this._initializeCreateForm = this._initializeCreateForm.bind(this);
    this._initializeEditForm = this._initializeEditForm.bind(this);
    this._isEditMode = this._isEditMode.bind(this);
    this._getInitialStageIndex = this._getInitialStageIndex.bind(this);
    this._loadCredentials = this._loadCredentials.bind(this);
    this._clearProbeData = this._clearProbeData.bind(this);
    this._onRequestProbeTree = this._onRequestProbeTree.bind(this);
    this._onRequestInitialDBProbeTree = this._onRequestInitialDBProbeTree.bind(this);
    this._onToggleNodeProbeTree = this._onToggleNodeProbeTree.bind(this);
    this._handleCredentialFormInputChanged = this._handleCredentialFormInputChanged.bind(this);
    this._handleScriptCredentialInputChanged = this._handleScriptCredentialInputChanged.bind(this);
    this._handleSaveNewScriptCredential = this._handleSaveNewScriptCredential.bind(this);
    this._onUpdateOrderedDataSchema = this._onUpdateOrderedDataSchema.bind(this);
    this._onToggleExcludeAttr = this._onToggleExcludeAttr.bind(this);
    this._validateSelectedTable = this._validateSelectedTable.bind(this);
    this._handleSinkConfigChanged = this._handleSinkConfigChanged.bind(this);
    this._handleTemplatizedAPIConfigChanged = this._handleTemplatizedAPIConfigChanged.bind(this);
    this._handleTemplatizedAPIModeChanged = this._handleTemplatizedAPIModeChanged.bind(this);

    //only needed for subs!!
    this._handleConnectionSelected = this._handleConnectionSelected.bind(this);
    this._handleChangeDestinationMode = this._handleChangeDestinationMode.bind(this);
    this._handleRequestCredentialCreation = this._handleRequestCredentialCreation.bind(this);
    this._handleRequestCredentialProbe = this._handleRequestCredentialProbe.bind(this);
    this._handleProbeBucketSelected = this._handleProbeBucketSelected.bind(this);
    this._handleCredSelectionChanged = this._handleCredSelectionChanged.bind(this);

    this.skipAutoRedirectionRef = { current: false };

    this._getSinkMappingAttributes = this._getSinkMappingAttributes.bind(this);
    this._buildSinkConfigFromDefaults = this._buildSinkConfigFromDefaults.bind(this);
    this._buildSinkConfigWithMapping = this._buildSinkConfigWithMapping.bind(this);
    this._setHCInSinkConfig = this._setHCInSinkConfig.bind(this);
    this._handleSinkMappingUpdated = this._handleSinkMappingUpdated.bind(this);
    this._handleTableSelected = this._handleTableSelected.bind(this);
    this._handleCollectionSelected = this._handleCollectionSelected.bind(this);

    this._handleFormDismissed = this._handleFormDismissed.bind(this);
    this._handleFormSubmitted = this._handleFormSubmitted.bind(this);
    this._handleEnterReviewStage = this._handleEnterReviewStage.bind(this);
    this._validateSelectConnection = this._validateSelectConnection.bind(this);
    this._validateSelectCredential = this._validateSelectCredential.bind(this);
    this._validateConfigureNoSQL = this._validateConfigureNoSQL.bind(this);
    this._validateFormCompletion = this._validateFormCompletion.bind(this);
    this._redirectAfterCompletion = this._redirectAfterCompletion.bind(this);
    this._getSinkName = this._getSinkName.bind(this);
    this._getPipelineFormPath = this._getPipelineFormPath.bind(this);
    this.handleToggleDestinationDraft = this.handleToggleDestinationDraft.bind(this);
  }

  componentDidMount() {
    if (this.props.sinkIdParam || this.props.dataSetIdParam) {
      this.setState({ isLoadingInitialData: true }, () => {
        if (this.props.sinkIdParam) {
          this.props.loadDataSinkWithDataSet(this.props.sinkIdParam);
        } else if (this.props.dataSetIdParam) {
          this.props.fetchDataSet(this.props.dataSetIdParam);
        }

        this._loadCredentials();
      });
    }

    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_COMMON);
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_COMMON);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      paging: { metaInfoById },
      selectedConnection,
      sinkConfig,
      topics,
      vendors,
      formData,
    } = this.props;
    const connectionMeta = metaInfoById[selectedConnection];
    if (
      this.state.isLoadingInitialData &&
      ((this.props.sinkIdParam &&
        this.props.addedDataSink &&
        (this.props.addedDataSink !== prevProps.addedDataSink ||
          this.props.dataSet?.custom_config !== prevProps.dataSet?.custom_config) &&
        (!this.props.addedDataSink.data_set_id || this.props.dataSet?.custom_config)) ||
        (this.props.dataSetIdParam &&
          this.props.dataSet &&
          this.props.dataSet !== prevProps.dataSet))
    ) {
      const vendor =
        vendors?.[this.props.addedDataSink?.vendor_id || this.props.dataSet?.vendor_id];
      const vendorName = vendor?.name;
      if (vendorName) {
        // Keep the loader visible until vendor info has loaded and the form
        // is fully initialized, to prevent a flash of the Select step.
        this.loadVendorInfo(vendorName).then(() => {
          this.setState({ isLoadingInitialData: false }, () => {
            if (this._isEditMode()) {
              this._initializeEditForm();
            } else {
              this._initializeCreateForm();
            }
          });
        });
      } else {
        this.setState({ isLoadingInitialData: false }, () => {
          if (this._isEditMode()) {
            this._initializeEditForm();
          } else {
            this._initializeCreateForm();
          }
        });
      }
    }

    // If vendor_endpoint arrived after initial load completed, re-init
    // to pick up the endpoint data for the dropdown.
    const becameReadyAfterInitialLoad =
      prevState.isLoadingInitialData && !this.state.isLoadingInitialData;
    if (
      this._isEditMode() &&
      !this.state.isLoadingInitialData &&
      !becameReadyAfterInitialLoad &&
      didVendorEndpointJustArrive(prevProps.addedDataSink, this.props.addedDataSink)
    ) {
      this._initializeEditForm();
    }

    if (this.props.addedDataSink && !prevProps.addedDataSink && !this.props.loadingVendorInfo) {
      const vendor = vendors?.[this.props.addedDataSink?.vendor_id];
      const vendorName = vendor?.name;
      if (vendorName) {
        this.loadVendorInfo(vendorName);
      }
    }

    if (this.props.selectedCredentialId !== prevProps.selectedCredentialId) {
      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        sinkConfig,
      });
    }

    if (
      connectionMeta &&
      connectionMeta.connectionCategory === CONNECTION_CATEGORY_FILE &&
      sinkConfig &&
      ((sinkConfig !== prevProps.sinkConfig &&
        sinkConfig.data_format !== prevProps.sinkConfig.data_format) ||
        this.props.orderedOutputSchema !== prevProps.orderedOutputSchema ||
        this.props.excludeByAttrName !== prevProps.excludeByAttrName)
    ) {
      const { dataSchema, orderedOutputSchema, excludeByAttrName } = this.props;
      const { data_format } = sinkConfig;

      if (UIUtils.formatsWithColumns.indexOf(data_format) > -1) {
        const newMapping = UIUtils.getSinkMappingFromAttributes(
          this._getSinkMappingAttributes(
            selectedConnection,
            orderedOutputSchema || dataSchema,
            sinkConfig,
          ),
          excludeByAttrName,
        ).obj;

        this._handleSinkMappingUpdated(
          JSONUtils.stringifyJson(assign({}, sinkConfig.mapping, newMapping)),
          false,
        );
      } else {
        // only set sink auto mapping when last mapping mode is not auto
        if (get(prevProps.sinkConfig, 'mapping.mode') !== 'auto') {
          this._handleSinkMappingUpdated(null, true);
        }
      }
    }

    if (
      (this.props.selectedTable &&
        (this.props.selectedTable !== prevProps.selectedTable ||
          (get(sinkConfig, 'mapping.mode') === 'manual' &&
            get(prevProps.sinkConfig, 'mapping.mode') === 'auto'))) ||
      (this.props.selectedTable &&
        prevProps.selectedTable &&
        this.props.selectedTable?.columns?.length !== prevProps.selectedTable?.columns?.length)
    ) {
      const { allowSinkAutoMapping } = connectionMeta;
      const mode = get(sinkConfig, 'mapping.mode');
      const auto = (!mode && allowSinkAutoMapping) || mode === 'auto';
      const editedColumns = filter(this.props.selectedTable.columns, (c) => c.attr);
      const newMapping = reduce(
        editedColumns,
        (result, c) => {
          return {
            ...result,
            [c.attr]: { ...result[c.attr], [c.name]: c.type },
          };
        },
        {},
      );

      if (!isEmpty(newMapping)) {
        this._handleSinkMappingUpdated(JSONUtils.stringifyJson(newMapping), auto);
      }
    }

    if (
      this.props.selectedCredentialId &&
      this.props.selectedCredentialId !== prevProps.selectedCredentialId &&
      this.props.credentialCreationStatus &&
      this.props.credentialCreationStatus.credential &&
      connectionMeta &&
      connectionMeta.connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT
    ) {
      this._handleCredSelectionChanged(
        this.props.credentialCreationStatus.credentialObj,
        true,
        true, // skipAutoRedirection - avoid credential probe after creation
      );
    }

    if (
      this.props.probeData &&
      prevProps.probeData !== this.props.probeData &&
      prevProps.formData &&
      (!prevProps.formData.credentialProbeStatus ||
        (prevProps.formData.credentialProbeStatus &&
          !prevProps.formData.credentialProbeStatus.isValid))
    ) {
      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        credentialProbeStatus: {
          isValid: true,
        },
      });
    }

    if (
      this.props.probeError &&
      prevProps.probeError !== this.props.probeError &&
      (!prevProps.formData.credentialProbeStatus ||
        (prevProps.formData.credentialProbeStatus &&
          prevProps.formData.credentialProbeStatus.isValid))
    ) {
      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        credentialProbeStatus: {
          isValid: false,
        },
      });
    }

    const dataSetId =
      (this.props.dataSet && this.props.dataSet.id) ||
      (this.props.addedDataSink && this.props.addedDataSink.data_set_id);

    if (
      dataSetId &&
      get(this.props.samples, `[${dataSetId}].lastUpdated`) &&
      get(this.props.samples, `[${dataSetId}].lastUpdated`) !==
        get(prevProps.samples, `[${dataSetId}].lastUpdated`)
    ) {
      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        toNested: get(this.props.samples, `[${dataSetId}].original.out`),
      });
    }

    const connectionCategory = connectionMeta && connectionMeta.connectionCategory;

    if (connectionCategory === CONNECTION_CATEGORY_KAFKA) {
      // Re-classify the saved sink topic once /probe/tree returns. Uses the
      // shared resolver so the topic-field name + format-tolerant matcher
      // stays in sync with the initial-load block in _initializeEditForm.
      // Dispatches are mode-exclusive: setting pick-existing clears any
      // create-new fields and vice versa, to preserve the invariant the
      // KafkaSinkConfig handlers rely on.
      //
      // Gate on `topics` only — `topics` and `queues` are written together by
      // processGetKafkaTopicsSuccess (same action), so a probe transition
      // always changes the topics reference. Watching `queues` here would
      // false-trigger on every render because DestinationSetupContainer maps
      // `probeResponse.queues ?? []` and `[]` is a fresh reference each
      // selector run, which loops componentDidUpdate (NEX-21516).
      if (topics && topics !== prevProps.topics) {
        const { paging } = this.props;
        const sinkConfigFields = paging.sinkConfigById[selectedConnection];
        const classification = classifySavedTopic(
          sinkConfig,
          sinkConfigFields,
          topics,
          this.props.queues,
        );

        if (classification.status === 'exists') {
          this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
            selectedTopic: classification.value,
            isNewTopicChecked: false,
            newTopicName: '',
            subscription: '',
          });
        } else if (classification.status === 'new') {
          this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
            isNewTopicChecked: true,
            newTopicName: classification.value,
            selectedTopic: null,
            subscription: sinkConfig.subscription || '',
          });
        }
      }

      // update kafka sink config
      if (
        formData &&
        formData !== prevProps.formData &&
        (formData.selectedTopic !== prevProps.formData.selectedTopic ||
          formData.isNewTopicChecked !== prevProps.formData.isNewTopicChecked ||
          formData.newTopicName !== prevProps.formData.newTopicName ||
          formData.subscription !== prevProps.formData.subscription)
      ) {
        const { paging, selectedConnection } = this.props;
        const sinkConfigFields = paging.sinkConfigById[selectedConnection];
        const topicFieldName = resolveSinkTopicFieldName(sinkConfigFields);

        // Pick which form-state field owns sinkConfig[topicFieldName] strictly
        // by mode — no fallback. The old `newTopicName || selectedTopic`
        // fallback would silently submit a stale pick as a "create new" payload
        // when the user ticked the checkbox before typing.
        let newSinkConfig = {
          ...sinkConfig,
          'ui.is_new_topic': formData.isNewTopicChecked,
          [topicFieldName]: formData.isNewTopicChecked
            ? formData.newTopicName
            : formData.selectedTopic,
        };

        // checking for subscription field
        let subscriptionField = sinkConfigFields.find((field) => field.name === 'subscription');
        if (!!subscriptionField) {
          newSinkConfig['subscription'] =
            formData.isNewTopicChecked && formData.subscription ? formData.subscription : null;
        }

        newSinkConfig = UIUtils.reduceConfig(newSinkConfig, sinkConfigFields);

        this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
          sinkConfig: newSinkConfig,
        });
      }
    }

    // sink mapping
    let { sinkMapping } = this.state;

    if (this._isEditMode() && !prevProps.addedDataSink && this.props.addedDataSink) {
      // load sink in edit mode
      let sinkConfig = has(this.props.addedDataSink, 'sink_config')
        ? this.props.addedDataSink.sink_config
        : null;

      sinkMapping = UIUtils.getSinkMappingFromAttributes(
        this._getSinkMappingAttributes(
          this.props.selectedConnection,
          this.props.dataSchema,
          sinkConfig,
        ),
      );

      let selectedBucket =
        this.props.selectedBucket !== null ? { ...this.props.selectedBucket } : {};

      if (sinkConfig) {
        if (sinkConfig.path) {
          selectedBucket.path = sinkConfig.path;
        } else if (sinkConfig.bucket) {
          let path = sinkConfig.bucket;

          if (sinkConfig.prefix) {
            const separator = path.endsWith('/') || sinkConfig.prefix.startsWith('/') ? '' : '/';
            path += `${separator}${sinkConfig.prefix}`;
          }

          selectedBucket.path = path;
        }
      }

      this.setState({
        sinkMapping,
      });

      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        sinkConfig: sinkConfig,
        selectedBucket,
      });
    } else if (
      this.props.addedDataSink !== prevProps.addedDataSink &&
      this.props.addedDataSink === null
    ) {
      let defaultSinkConfig = this._buildSinkConfigFromDefaults(this.props.selectedConnection);

      //TODO: this should belong in redux state so we dont
      if (prevProps.dataSchema !== this.props.dataSchema) {
        sinkMapping = UIUtils.getSinkMappingFromAttributes(
          this._getSinkMappingAttributes(
            this.props.selectedConnection,
            this.props.dataSchema,
            defaultSinkConfig,
          ),
        );
      }

      let sinkConfigWithMapping = this._buildSinkConfigWithMapping(
        defaultSinkConfig,
        sinkMapping,
        this.props.selectedConnection,
      );
      this.setState({
        sinkMapping,
      });

      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        selectedBucket: null,
        sinkConfig: sinkConfigWithMapping,
      });
    }

    // continue validation
    if (
      this.state.scriptCredentialAwaitingCreation &&
      this.props.credentialCreationStatus &&
      this.props.credentialCreationStatus.credential &&
      this.props.credentialCreationStatus !== prevProps.credentialCreationStatus
    ) {
      this._boundHandleSaveNewScriptCredential();
    }

    if (
      (this.state.selectCredentialAwaitingCreation &&
        this.props.credentialCreationStatus &&
        this.props.credentialCreationStatus.credential &&
        this.props.credentialCreationStatus !== prevProps.credentialCreationStatus) ||
      (this.state.selectCredentialAwaitingProbe &&
        this.props.credentialProbeStatus &&
        this.props.credentialProbeStatus.credential &&
        this.props.credentialProbeStatus !== prevProps.credentialProbeStatus)
    ) {
      this._boundValidateSelectCredential();
    }

    if (
      (this.state.awaitingSinkCreation &&
        this.props.addedDataSink &&
        this.props.addedDataSink !== prevProps.addedDataSink) ||
      (this.props.addedDataSinkError &&
        this.props.addedDataSinkError !== prevProps.addedDataSinkError) ||
      (this.state.awaitingSinkUpdate &&
        this.props.updatedDataSink &&
        this.props.updatedDataSink !== prevProps.updatedDataSink) ||
      (this.props.updatedDataSinkError &&
        this.props.updatedDataSinkError !== prevProps.updatedDataSinkError)
    ) {
      this._boundValidateFormCompletion();
    }
  }

  _getInitialState(selectedConnection = null) {
    const [selectedTemplate, connectionAuthFields] = (() => {
      const { selectedCredentialInfo, selectedConnection } = this.props;
      const effectiveConnectionType =
        (selectedCredentialInfo && selectedCredentialInfo._vendorConnectionType) ||
        selectedConnection;
      const { templates, items } = this.props.selectedConnection
        ? this.props.paging.connAuthById[effectiveConnectionType]
        : {};
      const template = templates && templates.length ? templates[0] : null;
      const templateId = template?.id;
      const fields =
        templates?.length > 1 && !!template
          ? items.filter((item) => template.auth_parameters.includes(item.param_id))
          : items;
      return [templateId, fields];
    })();
    return {
      selectedTemplate,
      sinkMapping: UIUtils.getSinkMappingFromAttributes(
        this._getSinkMappingAttributes(
          selectedConnection || this.props.selectedConnection,
          this.props.dataSchema,
          this.props.sinkConfig,
        ),
      ),
      isLoadingInitialData: false,
      connectionAuthFields,
      scriptCredentialAwaitingCreation: false,
      selectCredentialAwaitingCreation: false,
      selectCredentialAwaitingProbe: false,
      awaitingSinkCreation: false,
      awaitingSinkUpdate: false,
      isDestinationDraft: false,
      isDestinationDraftEdited: false,
    };
  }

  _initializeCreateForm(connectionType) {
    const {
      paging: { connAuthById },
      dataSet,
      selectedCredentialInfo,
      dataSchema,
      addedDataSink,
    } = this.props;
    const selectedConnection = connectionType || this.props.selectedConnection;
    const dataSetId = (dataSet && dataSet.id) || (addedDataSink && addedDataSink.data_set_id);
    let sinkConfig = null;

    if (dataSchema) {
      // this._setSinkFromAttributes(
      //   this.props.dataSchema,
      //   this.props.addedDataSink
      // );
      const sinkMapping = UIUtils.getSinkMappingFromAttributes(
        this._getSinkMappingAttributes(
          selectedConnection,
          this.props.dataSchema,
          this.props.sinkConfig,
        ),
      );

      const sinkConfigWithMapping = this._buildSinkConfigWithMapping(
        this.props.sinkConfig,
        sinkMapping,
      );

      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        selectedConnection,
        sinkConfig: sinkConfigWithMapping,
        scriptCredentialsData: {},
        dataSetId,
      });

      if (this.props.addedDataSink) {
        this.props.updateSinkInfo(this.props.addedDataSink.id, {
          sink_config: this.props.sinkConfig,
        });
      }

      this.setState({ sinkMapping });
    } else {
      sinkConfig = selectedConnection
        ? this._buildSinkConfigFromDefaults(selectedConnection)
        : null;

      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        selectedConnection,
        sinkConfig,
        scriptCredentialsData: {},
        dataSetId,
      });
    }

    const effectiveConnectionType =
      (selectedCredentialInfo && selectedCredentialInfo._vendorConnectionType) ||
      selectedConnection;

    const { templates, items } = selectedConnection ? connAuthById[effectiveConnectionType] : {};

    this.setState({
      connectionAuthFields: this.getActiveItems(templates, items),
    });

    if (dataSetId) {
      this.props.fetchDataSamples(dataSetId);
    }
  }

  _initializeEditForm() {
    const {
      paging: { metaInfoById, sinkConfigById, connAuthById },
      dataSet,
      dataSchema,
      formData,
      addedDataSink,
      selectedCredentialInfo,
      dataMap,
      sinkCredential,
      topics,
      queues,
      vendors,
    } = this.props;
    const dataSetId = (dataSet && dataSet.id) || (addedDataSink && addedDataSink.data_set_id);
    const sinkConnectionOrVendorType =
      addedDataSink && has(addedDataSink, 'sink_type')
        ? getConnectionOrVendorType(addedDataSink, vendors, 'sink_type', 'vendor_id')
        : null;

    const { templates, items } = sinkConnectionOrVendorType
      ? connAuthById[
          (selectedCredentialInfo && selectedCredentialInfo._vendorConnectionType) ||
            sinkConnectionOrVendorType
        ]
      : {};

    this.setState({
      connectionAuthFields: this.getActiveItems(templates, items),
    });

    const sinkCredentialVendorType =
      sinkCredential && has(sinkCredential, 'credentials_type')
        ? getConnectionOrVendorType(sinkCredential, vendors, 'credentials_type', 'vendor_id')
        : null;
    let sinkConfig = null;

    sinkConfig =
      addedDataSink && has(addedDataSink, 'sink_config') ? addedDataSink.sink_config : null;

    // convert legacy sink mapping format to the new one

    const mapping = get(sinkConfig, 'mapping');
    const attrMapping = get(mapping, 'mapping');
    const connectionCategory =
      metaInfoById[sinkConnectionOrVendorType] &&
      metaInfoById[sinkConnectionOrVendorType].connectionCategory;
    const isTemplatizedAPI =
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API ||
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT
        ? true
        : false;

    // If the sink has a vendor_endpoint at root, wait for vendor endpoint
    // details to load before initializing the form
    // (loadVendorInfo().then() will call _initializeEditForm again when ready).
    if (hasVendorEndpoint(addedDataSink) && this.state.isLoadingInitialData) {
      return;
    }

    if (isTemplatizedAPI === true && !(sinkConfig.sink_type === 'rest' && sinkConfig.vendor_id)) {
      sinkConfig = {
        sink_type: sinkConnectionOrVendorType,
      };
      if (has(addedDataSink, 'vendor_endpoint')) {
        sinkConfig.vendor_endpoint_id = addedDataSink.vendor_endpoint;
      } else if (has(addedDataSink, 'vendor_endpoint_id')) {
        sinkConfig.vendor_endpoint_id = addedDataSink.vendor_endpoint_id;
      }
      if (has(addedDataSink, 'template_config')) {
        sinkConfig = {
          ...sinkConfig,
          ...addedDataSink.template_config,
        };
      }
    }

    if (isTemplatizedAPI === false && !isEmpty(attrMapping) && isString(values(attrMapping)[0])) {
      const isDatabase = connectionCategory === CONNECTION_CATEGORY_DATABASE;
      const newAttrMapping = mapValues(attrMapping, (value) =>
        isDatabase ? { [value]: null } : [value],
      );
      sinkConfig = {
        ...sinkConfig,
        mapping: { ...mapping, mapping: newAttrMapping },
      };
    }

    let selectedBucket = null;
    let selectedTopic;
    let isNewTopicChecked;
    let newTopicName;
    let subscription;

    if (isTemplatizedAPI === false && sinkConfig) {
      // update sinkConfig of legacy sinks
      const { bucket, prefix, path } = sinkConfig;
      let effectivePath;

      if (path) {
        effectivePath = path;
      } else if (bucket) {
        effectivePath = bucket;

        if (prefix) {
          const separator = effectivePath.endsWith('/') || prefix.startsWith('/') ? '' : '/';
          effectivePath += `${separator}${prefix}`;
        }
      }

      sinkConfig = omit(sinkConfig, ['bucket', 'prefix']);

      if (!isNil(effectivePath)) {
        sinkConfig.path = effectivePath;
        selectedBucket = { path: effectivePath };
      }

      // Pre-fill the picker / create-new mode from the saved sink config.
      // Uses the shared resolver so:
      //   - JMS (sinkConfig['target.name']) pre-fills, not just Kafka (.topic)
      //   - Pub/Sub matches even when the saved value is a full resource path
      //     (projects/<p>/topics/<n>) but probe returns short names, or vice versa
      //   - Queues are considered for JMS where target.type='queue'
      // If topics/queues haven't loaded yet, classifySavedTopic returns
      // 'unknown' and the probe-arrives reconciler in componentDidUpdate
      // classifies once the lists land.
      if (connectionCategory === CONNECTION_CATEGORY_KAFKA) {
        const sinkConfigFields = sinkConfigById[sinkConnectionOrVendorType];
        const classification = classifySavedTopic(sinkConfig, sinkConfigFields, topics, queues);

        if (classification.status === 'exists') {
          selectedTopic = classification.value;
        } else if (classification.status === 'new') {
          isNewTopicChecked = true;
          newTopicName = classification.value;
        }
      }

      if (has(sinkConfig, 'subscription')) {
        subscription = sinkConfig.subscription;
      }
    }

    // set orderedOutputSchema and excludeByAttrName from fields_order
    let orderedOutputSchema;
    let excludeByAttrName;

    if (
      isTemplatizedAPI === false &&
      dataSchema &&
      UIUtils.formatsWithColumns.indexOf(get(sinkConfig, 'data_format')) > -1
    ) {
      const fieldsOrder = get(sinkConfig, 'mapping.fields_order');
      const attrsByName = groupBy(this.props.dataSchema, '__name');
      orderedOutputSchema = compact(
        map(fieldsOrder, (attrName) => attrsByName[attrName] && attrsByName[attrName][0]),
      );

      // append excluded attrs to orderedOutputSchema
      forEach(attrsByName, (attrs, name) => {
        if (orderedOutputSchema && attrs && fieldsOrder && fieldsOrder.indexOf(name) < 0) {
          orderedOutputSchema.push(attrs[0]);
        }
      });

      if (isEmpty(orderedOutputSchema)) {
        orderedOutputSchema = null;
      }

      // an attr is excluded if it doesn't exist in sink_config.mapping.mapping
      excludeByAttrName = mapValues(
        attrsByName,
        (attrs, name) => !has(sinkConfig, `mapping.mapping['${name}']`),
      );

      if (isEmpty(excludeByAttrName)) {
        excludeByAttrName = null;
      }
    }

    // script creds
    let scriptCredentialsData = {};

    if (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT && this.props.addedDataSink) {
      const decodedSink = UIUtils.buildTemplatizedScriptConfigFromSource(
        this.props.addedDataSink,
        sinkConfigById[sinkConnectionOrVendorType],
        this.props.credentialEntities,
      );

      scriptCredentialsData = decodedSink.scriptCredentialsData;
      sinkConfig = { ...sinkConfig, ...decodedSink.templatizedScriptConfig };
    }

    const formDataToUpdate = {
      dataSetId,
      sinkConfig,
      selectedBucket,
      orderedOutputSchema,
      excludeByAttrName,
      selectedTopic,
      isNewTopicChecked,
      newTopicName,
      subscription,
      scriptCredentialsData,
    };

    if (!has(formData, 'addedDataSinkId') && addedDataSink) {
      formDataToUpdate.addedDataSinkId = addedDataSink.id;
    }
    if (!has(formData, 'addedDatasetId')) {
      formDataToUpdate.addedDatasetId = dataSet && dataSet.id;
    }

    if (!has(formData, 'selectedConnection') && has(addedDataSink, 'sink_type')) {
      formDataToUpdate.selectedConnection = sinkConnectionOrVendorType;
    }

    if (!has(formData, 'selectedCredential') && sinkCredential) {
      formDataToUpdate.selectedCredential = sinkCredential;
      formDataToUpdate.selectedCredentialInfo = {
        ...pick(sinkCredential, [
          'id',
          'name',
          'credentials_type',
          'verified_at',
          'verified_status',
        ]),
      };
      formDataToUpdate.selectedCredentialInfo.credentials_type = sinkCredentialVendorType;
      formDataToUpdate.selectedCredentialId = sinkCredential.id;
    }

    if (!has(formData, 'credentialProbeStatus') && addedDataSink.data_credentials) {
      formDataToUpdate.credentialProbeStatus = {
        credential: addedDataSink.data_credentials.id,
        isValid: true,
        message: null,
      };
    }

    if (dataMap && !has(formData, 'dataMapSettings')) {
      //this is dyanamic data map sink.
      let dataMapCopy = cloneDeep(dataMap);
      let dataDefaults = mapKeys(dataMapCopy.data_defaults, (v, k) => 'data_defaults.' + k);
      formDataToUpdate.dataMapSettings = {
        name: dataMapCopy.name,
        map_primary_key: dataMapCopy.map_primary_key,
        ...dataDefaults,
      };
      if (dataMapCopy.description) {
        formDataToUpdate.dataMapSettings.description = dataMapCopy.description;
      }
    }

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, formDataToUpdate);
    const sinkConnectionCategory =
      metaInfoById[sinkConnectionOrVendorType] &&
      metaInfoById[sinkConnectionOrVendorType].connectionCategory;

    this.props.setFormStageIndex(
      FORM_TYPE_EDIT_DESTINATION,
      this._getInitialStageIndex(sinkConnectionCategory),
    );

    if (dataSetId) {
      this.props.fetchDataSamples(dataSetId);
    }
  }

  _isEditMode() {
    return !!this.props.sinkIdParam;
  }

  _getInitialStageIndex(connectionCategory) {
    if (!this._isEditMode()) {
      return 0;
    }

    return connectionCategory === CONNECTION_CATEGORY_FILE ||
      connectionCategory === CONNECTION_CATEGORY_VECTOR_DB ||
      connectionCategory === CONNECTION_CATEGORY_REST ||
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API ||
      connectionCategory === CONNECTION_CATEGORY_DATABASE ||
      connectionCategory === CONNECTION_CATEGORY_NOSQL ||
      connectionCategory === CONNECTION_CATEGORY_KAFKA
      ? 2
      : 1;
  }

  _loadCredentials() {
    this.props.loadDataCredentials(ACLQueryModeTypes.MODE_COLLABORATOR.key);
  }

  _clearProbeData() {
    this.props.resetFormData(FORM_TYPE_EDIT_DESTINATION, [
      'selectedCredential',
      'selectedCredentialId',
      'sinkConfig',
      'selectedBucket',
      'isNewTable',
      'selectedTable',
    ]);
    this.props.probeClearAll();
  }

  _onRequestProbeTree(isRoot = true, selectedFolder = null) {
    this.props.probeGetConnectionTree(this.props.selectedCredentialId, isRoot, selectedFolder);
  }

  _onRequestInitialDBProbeTree(selectedFolder = null) {
    this.props.probeGetInitialDBTree(this.props.selectedCredentialId, true, selectedFolder);
  }

  _onToggleNodeProbeTree(node) {
    this.props.probeToggleTreeNode(this.props.selectedCredentialId, node);
  }

  _handleCredentialFormInputChanged(input) {
    let { selectedConnection, paging } = this.props;
    let { templates, items } = selectedConnection ? paging.connAuthById[selectedConnection] : null;
    const fields = this.getActiveItems(templates, items, this.state.selectedTemplate);

    const id = input?.selectedCredentialInfo?.template;
    const info = { ...input };
    if (!isNil(id) && id != this.state.selectedTemplate) {
      this.state.connectionAuthFields.forEach((field) => {
        delete info?.selectedCredentialInfo[field.name];
      });
    }

    this.props.updateFormData(
      FORM_TYPE_EDIT_DESTINATION,
      UIUtils.fillConditionalDefault(info, fields),
    );
  }

  onChangeTemplate = (id) => {
    const { selectedConnection, selectedCredentialInfo, paging } = this.props;
    const effectiveConnectionType =
      (selectedCredentialInfo && selectedCredentialInfo._vendorConnectionType) ||
      selectedConnection;
    const { templates, items } = effectiveConnectionType
      ? paging.connAuthById[effectiveConnectionType]
      : {};
    this.setState({
      selectedTemplate: Number(id),
      connectionAuthFields: this.getActiveItems(templates, items, Number(id)),
    });
  };

  getActiveItems = (templates, items, selectedTemplate) => {
    const template = templates?.find((t) => t.id === selectedTemplate) || templates?.[0];
    return templates && templates?.length > 1 && template
      ? items.filter((item) => template.auth_parameters.includes(item.param_id))
      : items;
  };

  _handleScriptCredentialInputChanged(name, input) {
    const { scriptCredentialsData, updateFormData } = this.props;
    const thisCredentialData = scriptCredentialsData[name] || {};
    const updatedCredentialData = { ...thisCredentialData, ...input };
    const newData = {
      ...scriptCredentialsData,
      [name]: updatedCredentialData,
    };

    if (isInteger(updatedCredentialData.selectedCredentialId)) {
      this._handleTemplatizedAPIConfigChanged(name, updatedCredentialData.selectedCredentialId);
    }

    updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      scriptCredentialsData: newData,
    });
  }

  _handleSaveNewScriptCredential(name) {
    const {
      paging: { sinkConfigById, connAuthById },
      scriptCredentialsData,
      templatizedScriptConfig,
      selectedConnection,
      credentialCreationStatus,
      setFormError,
      updateFormData,
    } = this.props;
    const { scriptCredentialAwaitingCreation } = this.state;
    const thisCredentialData = scriptCredentialsData[name] || {};
    const { selectedCredentialInfo } = thisCredentialData;

    // required field validation
    const { credential_type } = find(sinkConfigById[selectedConnection], {
      name,
    });
    const { templates, items } =
      connAuthById[
        (selectedCredentialInfo && selectedCredentialInfo._vendorConnectionType) || credential_type
      ];
    const connectionAuthFields = this.getActiveItems(templates, items);
    this.setState({ connectionAuthFields });
    const missingFields = getEmptyRequiredFields(connectionAuthFields, selectedCredentialInfo);

    // check if credential name has been added or not
    if (!get(selectedCredentialInfo, 'cred_name')) {
      missingFields.unshift({
        name: 'cred_name',
        display_name: 'Credential Name',
      });
    }

    const errorMessage = formatValidationErrorMessage(missingFields);

    if (errorMessage) {
      setFormError(FORM_TYPE_EDIT_DESTINATION, errorMessage);
      return;
    }

    // create new credential
    this._boundHandleSaveNewScriptCredential = this._handleSaveNewScriptCredential.bind(
      this,
      ...arguments,
    );

    if (scriptCredentialAwaitingCreation) {
      this.setState({
        scriptCredentialAwaitingCreation: false,
      });

      // if creation failed, error
      if (credentialCreationStatus.message) {
        setFormError(FORM_TYPE_EDIT_DESTINATION, credentialCreationStatus.message);
        return;
      }

      // if cred created, select it
      const updatedCredentialData = {
        ...thisCredentialData,
        isNewCredential: false,
        selectedCredentialId: credentialCreationStatus.credential,
      };
      const newData = {
        ...scriptCredentialsData,
        [name]: updatedCredentialData,
      };
      const newTemplatizedScriptConfig = {
        ...templatizedScriptConfig,
        [name]: updatedCredentialData.selectedCredentialId,
      };

      updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        scriptCredentialsData: newData,
        templatizedScriptConfig: newTemplatizedScriptConfig,
        credentialCreationStatus: null,
      });

      return;
    }

    // request cred creation
    this.setState(
      {
        scriptCredentialAwaitingCreation: true,
      },
      () => {
        const credentialId = credentialCreationStatus ? credentialCreationStatus.credential : null;
        this._handleRequestCredentialCreation(
          {
            ...selectedCredentialInfo,
            _vendorConnectionType: credential_type,
          },
          credentialId,
        );
      },
    );
  }

  _handleNewDropboxCredential(callback) {
    DropboxService.authenticate(callback, this.props.selectedCredentialInfo);
  }

  _handleNewBoxCredential(callback) {
    BoxService.authenticate(callback, this.props.selectedCredentialInfo);
  }

  _onUpdateOrderedDataSchema(attrs) {
    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      orderedOutputSchema: attrs,
    });
  }

  _onToggleExcludeAttr(attrName) {
    const excludeByAttrName = this.props.excludeByAttrName || {};

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      excludeByAttrName: {
        ...excludeByAttrName,
        [attrName]: !excludeByAttrName[attrName],
      },
    });
  }

  _validateSelectedTable() {
    let { selectedTable } = this.props;
    return (
      !!selectedTable &&
      !!selectedTable.columns.length &&
      reduce(
        selectedTable.columns,
        (result, c) => {
          return result || !!c.attr;
        },
        true,
      )
    );
  }

  onInputChange = ({ property, value }) => {
    const { sinkConfig } = this.props;
    const key = property === 'sourceName' ? 'name' : 'description';
    this._handleSinkConfigChanged({
      ...sinkConfig,
      [key]: value,
    });
  };

  onSaveAdvancedMode = async (newSinkConfig) => {
    // NEX-21470: the JSON drawer's parsed payload is the user's source of
    // truth. Bypass _handleSinkConfigChanged, which applies form-mode side
    // effects (UIUtils.fillConditionalDefault + _setHCInSinkConfig) that
    // would silently inject defaults on top of the user's paste.
    const sinkConfigWithCodeMode = withUiModeCode(newSinkConfig);
    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      sinkConfig: sinkConfigWithCodeMode,
    });
  };

  _handleSinkConfigChanged(newSinkConfig, field = null, value = null) {
    let { selectedConnection, paging } = this.props;
    let sinkConfigFields = selectedConnection ? paging.sinkConfigById[selectedConnection] : null;
    let sinkConfig = UIUtils.fillConditionalDefault(newSinkConfig, sinkConfigFields, field, value);
    sinkConfig = withUiModeDefault(sinkConfig);

    this._setHCInSinkConfig(sinkConfig, selectedConnection);

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      sinkConfig,
    });
  }

  _handleTemplatizedAPIConfigChanged(name, newValue) {
    const { sinkConfig } = this.props;
    const value = 'parameters.to_email' === name ? newValue.trim() : newValue;

    this._handleSinkConfigChanged({ ...sinkConfig, [name]: value }, name, value);
  }

  _handleTemplatizedAPIModeChanged(isAdvancedMode, newConfig = {}) {
    const {
      paging: { metaInfoById },
      selectedConnection,
      updateFormData,
    } = this.props;
    const { sinkMapping } = this.state;
    let sinkConfig;

    if (isAdvancedMode) {
      const { vendorId } = metaInfoById[selectedConnection];
      sinkConfig = this._buildSinkConfigFromDefaults('rest', true);
      sinkConfig = this._buildSinkConfigWithMapping(sinkConfig, sinkMapping, 'rest');
      sinkConfig.vendor_id = vendorId;
      sinkConfig = {
        ...sinkConfig,
        ...newConfig,
      };
    } else {
      sinkConfig = this._buildSinkConfigFromDefaults(selectedConnection, true);
      sinkConfig = {
        ...sinkConfig,
        ...newConfig,
      };
    }

    updateFormData(FORM_TYPE_EDIT_DESTINATION, { sinkConfig });
  }

  updateSinkConfig(selectedConnection) {
    let sinkConfig = this._buildSinkConfigFromDefaults(selectedConnection, true);

    sinkConfig = this._buildSinkConfigWithMapping(
      sinkConfig,
      this.state.sinkMapping,
      selectedConnection,
    );

    // select the default vendor/script endpoint
    const meta = this.props.paging.metaInfoById[selectedConnection];

    if (
      meta &&
      (meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT ||
        meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API)
    ) {
      sinkConfig = {
        ...sinkConfig,
        mapping: { mode: 'auto' },
      };

      const sinkConfigFields = this.props.paging.sinkConfigById[selectedConnection];
      const endpointField = find(sinkConfigFields, {
        name: 'vendor_endpoint_id',
      });

      if (meta && endpointField && !isEmpty(endpointField.options)) {
        let endpointId;

        switch (meta.connectionCategory) {
          case CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT: {
            if (endpointField.options.length === 1) {
              endpointId = endpointField.options[0].value;
            }
            break;
          }

          // case CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT: {
          //   const endpointOption = find(
          //     endpointField.options,
          //     (option) =>
          //       this.props.vendorEndpoints[option.value] &&
          //       this.props.vendorEndpoints[option.value].name ===
          //         EMAIL_ENDPOINT_NAME
          //   );
          //   endpointId = endpointOption && endpointOption.value;
          //   break;
          // }

          case CONNECTION_CATEGORY_TEMPLATIZED_API: {
            // select the first endpoint (options are already in display order)
            endpointId = endpointField.options[0].value;
          }
        }

        if (endpointId) {
          const endpointConfig = UIUtils.configWithMissingDefaults(
            { vendor_endpoint_id: endpointId },
            sinkConfigFields,
          );

          sinkConfig = {
            ...sinkConfig,
            ...endpointConfig,
          };
        }
      }
    }

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      selectedConnection: selectedConnection,
      sinkConfig: sinkConfig,
      dataMapSettings: this.props.dataMapSettings || {},
    });
  }

  _handleConnectionSelected(selectedConnection) {
    let newDefaultState = this._getInitialState(selectedConnection);
    this.setState(
      {
        ...newDefaultState,
      },
      () => {
        this.props.resetFormData(
          FORM_TYPE_EDIT_DESTINATION,
          [
            'credentialCreationStatus',
            'credentialProbeStatus',
            'addedDataSinkId',
            'selectedCredential',
            'sinkConfig',
            'selectedCredentialInfo',
            'selectedCredentialId',
            'isNewCredential',
          ],
          false,
        );
        this.updateSinkConfig(selectedConnection);
      },
    );
  }

  _handleChangeDestinationMode(attachDestination = false) {
    let sinkConfig = this._buildSinkConfigFromDefaults(null, true);

    this.props.resetFormData(FORM_TYPE_EDIT_DESTINATION, [
      'credentialCreationStatus',
      'credentialProbeStatus',
      'addedDataSinkId',
      'selectedCredential',
      'sinkConfig',
    ]);

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      selectedConnection: null,
      // destinationMode: destinationMode,
      sinkConfig: sinkConfig,
    });
  }

  onResetForm = () => {
    const fields = this._isEditMode()
      ? [
          'isNewTable',
          'selectedBucket',
          'selectedCredential',
          'credentialProbeStatus',
          'credentialCreationStatus',
        ]
      : [
          'credentialCreationStatus',
          'credentialProbeStatus',
          'addedDataSinkId',
          'selectedTable',
          'isNewTable',
          'sinkConfig',
          'selectedBucket',
          'selectedCredential',
        ];

    this.props.resetFormData(FORM_TYPE_EDIT_DESTINATION, fields);
  };

  _handleCredSelectionChanged(
    selectedCredential,
    resetValidationStatus = true,
    skipAutoRedirection,
  ) {
    this.skipAutoRedirectionRef.current = skipAutoRedirection;

    let { selectedConnection, paging, updateFormData } = this.props;
    let { templates, items } = selectedConnection ? paging.connAuthById[selectedConnection] : [];
    const connectionAuthFields = this.getActiveItems(templates, items);

    this.onResetForm();

    this.props.probeClearAll(this.props.selectedCredentialId);
    this.setState({ connectionAuthFields });

    if (selectedCredential.id) {
      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        isNewCredential: false,
        selectedCredential,
        selectedCredentialId: selectedCredential.id,
        selectedCredentialInfo: UIUtils.configWithMissingDefaults(
          selectedCredential,
          connectionAuthFields,
        ),
      });
      this.updateSinkConfig(selectedConnection);
      this._verifySelectedCredential(selectedCredential.id);
    } else {
      updateFormData(FORM_TYPE_EDIT_DESTINATION, { selectedCredential });
    }
  }

  _goToNextStep = () => {
    const { setFormStageIndex, currentStage } = this.props;
    setFormStageIndex(FORM_TYPE_EDIT_DESTINATION, currentStage + 1);
  };

  _verifySelectedCredential = async (credentialId) => {
    const { verifyCredentialSilent, setFormError, setFormAwaitingValidation } = this.props;

    if (this.skipAutoRedirectionRef.current) return;

    setFormAwaitingValidation(FORM_TYPE_EDIT_DESTINATION, true);
    try {
      await verifyCredentialSilent(credentialId);
      setFormError(FORM_TYPE_EDIT_DESTINATION, null);
      this._goToNextStep();
    } catch (error) {
      const msg = error.message || 'Unable to authenticate.';
      setFormError(FORM_TYPE_EDIT_DESTINATION, msg);
    } finally {
      setFormAwaitingValidation(FORM_TYPE_EDIT_DESTINATION, false);
    }
  };

  _handleRequestCredentialCreation(credInfo, credentialId) {
    const credentialInfo = clone(credInfo);
    let { selectedConnection } = this.props;
    if (credInfo.template) {
      credentialInfo.auth_template_id = Number(credInfo.template);
    }
    let sinkConfig = this._buildSinkConfigFromDefaults(selectedConnection);
    this.props.resetFormData(FORM_TYPE_EDIT_DESTINATION, [
      'credentialCreationStatus',
      'credentialProbeStatus',
      'addedDataSinkId',
      'selectedCredential',
    ]);

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      sinkConfig: sinkConfig,
    });

    if (credentialId) {
      this.props.updateCredentialFromForm(FORM_TYPE_EDIT_DESTINATION, credentialId, credentialInfo);
    } else {
      const connectionType =
        (credentialInfo && credentialInfo._vendorConnectionType) || selectedConnection;

      this.props.addCredentialFromForm(FORM_TYPE_EDIT_DESTINATION, connectionType, credentialInfo);
    }
  }

  _handleProbeBucketSelected(selectedBucket) {
    const { paging, selectedConnection } = this.props;
    const sinkConfigFields = paging.sinkConfigById[selectedConnection];
    const path = selectedBucket && selectedBucket.path;
    const displayPath = selectedBucket && selectedBucket.displayPath;
    const mimeType = selectedBucket && selectedBucket.mimeType;
    const idPath = selectedBucket && selectedBucket.idPath; // For leaf-ID-only connectors (Google Drive)

    const sinkConfigWithBucket = assign({}, this.props.sinkConfig, {
      path,
      'ui.display_path': displayPath,
      'ui.selected_file_mime_type': mimeType,
      // Save the resolved ID path for leaf-ID-only connectors (Google Drive)
      // This allows future edits to skip iterative probing
      ...(idPath ? { 'ui.id_path': idPath } : {}),
    });
    // omit fields which shouldn't be rendered
    const visibleFieldData = UIUtils.reduceConfig(sinkConfigWithBucket, sinkConfigFields);
    const finalFieldData = UIUtils.configWithMissingDefaults(visibleFieldData, sinkConfigFields);

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      sinkConfig: finalFieldData,
      selectedBucket,
    });
  }

  _handleCollectionSelected(selectedCollection, isNewCollection) {
    const {
      paging: { metaInfoById },
      selectedConnection,
      sinkConfig,
    } = this.props;
    const connectionMeta = metaInfoById[selectedConnection] || {};
    let bucketObject = {
      collection: null,
      database: null,
    };

    //TODO: Need to fix with Database name
    if (selectedCollection) {
      const { name, id, ...selectedCollectionConfig } = selectedCollection;
      bucketObject = assign({}, bucketObject, selectedCollectionConfig);
      bucketObject.collection = has(selectedCollection, 'name') ? selectedCollection.name : null;
    }
    let sinkConfigWithBucket = assign({}, sinkConfig, bucketObject);

    if (
      connectionMeta.allowSinkAutoMapping &&
      get(sinkConfigWithBucket, 'mapping.mode') !== 'manual'
    ) {
      sinkConfigWithBucket.mapping = { mode: 'auto' };
    }

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      sinkConfig: sinkConfigWithBucket,
      selectedBucket: bucketObject,
      isNewCollection,
      selectedCollection,
    });
  }

  _handleTableSelected(selectedTable, isNewTable) {
    const {
      paging: { metaInfoById },
      selectedConnection,
      sinkConfig,
      addedDataSink,
    } = this.props;
    const connectionMeta = metaInfoById[selectedConnection] || {};
    let bucketObject = {
      table: null,
      database: null,
    };
    //TODO: Need to fix with Database name
    if (selectedTable) {
      const { columns, name, id, ...selectedTableConfig } = selectedTable;
      bucketObject = assign({}, bucketObject, selectedTableConfig);
      bucketObject.table = has(selectedTable, 'name') ? selectedTable.name : null;

      let _isEditMode = this._isEditMode();
      let previousTableName = '';
      // Original Value of Mode = 'addedDataSink.sink_config.mapping.mode'
      let previousMode = get(sinkConfig, 'mapping.mode', 'manual');
      if (_isEditMode) {
        previousTableName = get(addedDataSink, 'sink_config.table');
      }
      let shouldOverrideMapping = false;

      // If Table change, then override mode value to manuall
      // Don't override if it's a new table
      if (_isEditMode && !!previousTableName && previousTableName !== name && !isNewTable) {
        shouldOverrideMapping = true;
      }
      // shouldOverrideMapping === false,
      // set mode value to initial edit mode value

      bucketObject.mapping = UIUtils.buildMapping(
        connectionMeta.connectionCategory,
        sinkConfig.data_format,
        sinkConfig.mapping && sinkConfig.mapping.mapping
          ? sinkConfig.mapping.mapping
          : reduce(
              selectedTable.columns,
              (acc, c) => {
                if (c.attr) {
                  return {
                    ...acc,
                    [c.attr]: { ...acc[c.attr], [c.name]: c.type },
                  };
                }

                return acc;
              },
              {},
            ),
        sinkConfig.mapping,
        shouldOverrideMapping ? { mode: 'manual' } : { mode: previousMode },
      );
    }
    let sinkConfigWithBucket = assign({}, sinkConfig, bucketObject);

    if (
      connectionMeta.allowSinkAutoMapping &&
      get(sinkConfigWithBucket, 'mapping.mode') !== 'manual'
    ) {
      sinkConfigWithBucket.mapping = { mode: 'auto' };
    }

    this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
      sinkConfig: sinkConfigWithBucket,
      selectedBucket: bucketObject,
      isNewTable,
      selectedTable,
    });
  }

  _getSinkMappingAttributes(selectedConnection, allAttributes, sinkConfig) {
    const {
      paging: { metaInfoById },
    } = this.props;
    const { connectionCategory } = metaInfoById[selectedConnection] || {};

    if (
      connectionCategory === CONNECTION_CATEGORY_DATABASE ||
      (connectionCategory === CONNECTION_CATEGORY_FILE &&
        UIUtils.formatsWithColumns.indexOf(sinkConfig && sinkConfig.data_format) > -1)
    ) {
      // exclude attributes controlled by arrays
      const attributesByName = groupBy(allAttributes, '__name');
      return filter(
        allAttributes,
        ({ __controlledBy }) =>
          !attributesByName[__controlledBy] ||
          attributesByName[__controlledBy][0].__type !== 'array',
      );
    }

    return allAttributes;
  }

  _buildSinkConfigFromDefaults(selectedConnection, buildNew = false) {
    let currentSinkConfig;

    if (buildNew) {
      const sinkMapping = UIUtils.getSinkMappingFromAttributes(
        this._getSinkMappingAttributes(selectedConnection, this.props.dataSchema),
      );

      const sinkConfigWithMapping = this._buildSinkConfigWithMapping(
        null,
        sinkMapping,
        selectedConnection,
      );
      const meta = this.props.paging.metaInfoById[selectedConnection];

      if (
        meta &&
        meta.connectionCategory === CONNECTION_CATEGORY_DATABASE &&
        meta.allowSinkAutoMapping
      ) {
        sinkConfigWithMapping.mapping = { mode: 'auto' };
      }

      currentSinkConfig = sinkConfigWithMapping;
    } else {
      currentSinkConfig = { ...this.props.sinkConfig };
    }

    let sinkConfigFields = selectedConnection
      ? this.props.paging.sinkConfigById[selectedConnection]
      : [];

    currentSinkConfig = UIUtils.configWithMissingDefaults(currentSinkConfig, sinkConfigFields);
    currentSinkConfig.sink_type = selectedConnection;

    this._setHCInSinkConfig(currentSinkConfig, selectedConnection);

    return currentSinkConfig;
  }

  _buildSinkConfigWithMapping(sinkConfig, sinkMapping, conn) {
    const {
      paging: { metaInfoById },
      selectedConnection,
    } = this.props;
    const connectionMeta = metaInfoById[conn || selectedConnection] || {};
    const newSinkConfig = { ...sinkConfig };

    if (connectionMeta.connectionCategory === CONNECTION_CATEGORY_NOSQL) {
      return newSinkConfig;
    }

    if (!newSinkConfig.mapping) {
      const _isEditMode = this._isEditMode();
      const mappingForBuild = UIUtils.getMappingForBuild(
        connectionMeta.connectionCategory,
        sinkMapping,
      );

      newSinkConfig.mapping = UIUtils.buildMapping(
        connectionMeta.connectionCategory,
        newSinkConfig.data_format,
        mappingForBuild,
        null,
        _isEditMode ? {} : { mode: 'manual' },
      );
    }

    return newSinkConfig;
  }

  _setHCInSinkConfig(sinkConfig, selectedConnection) {
    const {
      paging: { sinkConfigById },
      currentOrg,
    } = this.props;
    const sinkConfigFields = selectedConnection ? sinkConfigById[selectedConnection] : [];
    const hcFields = filter(sinkConfigFields, (field) =>
      Constants.HC_SINK_FIELD_NAMES.includes(field.name),
    );

    if (UIUtils.isHCEnabled(currentOrg.id, selectedConnection, false)) {
      sinkConfig = UIUtils.configWithMissingDefaults(sinkConfig, hcFields);

      forEach(hcFields, (field) => {
        if (!UIUtils.shouldRenderField(field, sinkConfig)) {
          delete sinkConfig[field.name];
        }
      });
    } else {
      forEach(hcFields, (field) => {
        delete sinkConfig[field.name];
      });
    }
  }

  /**
   * @param {boolean} fromContinueValidation - Form already set awaitingValidation on Continue;
   *   do not clear on success (onSuccess does); clear only on probe failure.
   */
  _handleRequestCredentialProbe(credentialId, fromContinueValidation = false) {
    if (!credentialId) {
      return;
    }

    AnalyticsUtils.captureCustomEvent('data_sink_credential_selection', {
      credential_id: credentialId,
      connection_type: this.props.selectedConnection,
    });

    //build source config based on selected credential info
    const { sinkConfig, setFormAwaitingValidation } = this.props;

    let newSinkConfig = this._buildSinkConfigFromDefaults(this.props.selectedConnection, true);

    newSinkConfig = {
      ...newSinkConfig,
      ...sinkConfig,
    };

    this.props.resetFormData(
      FORM_TYPE_EDIT_DESTINATION,
      ['credentialProbeStatus', 'addedDataSinkId'],
      false,
    );

    this.props.updateFormData(
      FORM_TYPE_EDIT_DESTINATION,
      {
        sinkConfig: newSinkConfig,
      },
      false,
    );

    if (!fromContinueValidation) {
      setFormAwaitingValidation(FORM_TYPE_EDIT_DESTINATION, true);
    }

    const probePromise = this.props.verifyCredentialFromForm(
      FORM_TYPE_EDIT_DESTINATION,
      credentialId,
    );

    probePromise.catch(() => {
      if (fromContinueValidation) {
        setFormAwaitingValidation(FORM_TYPE_EDIT_DESTINATION, false);
      }
    });

    probePromise.finally(() => {
      if (!fromContinueValidation) {
        setFormAwaitingValidation(FORM_TYPE_EDIT_DESTINATION, false);
      }
    });
  }

  _handleSinkMappingUpdated(newSinkMapping, auto) {
    //if new text is not a valid json object dont add it to sink mapping
    let sinkMappingObj = JSONUtils.parseJson(newSinkMapping);
    let sinkMapping = {
      str: newSinkMapping,
      obj: sinkMappingObj ? sinkMappingObj : this.state.sinkMapping.obj,
    };
    const {
      paging: { metaInfoById },
      selectedConnection,
      sinkConfig,
    } = this.props;
    const connectionMeta = metaInfoById[selectedConnection] || {};

    //TODO: Evaluate if this auto property is necessary in the new FormatCard component
    if (auto) {
      this.setState({ sinkMapping }, () => {
        let sinkConfigAuto = {
          ...sinkConfig,
          mapping: { mode: 'auto' },
        };

        if (sinkMappingObj && sinkMappingObj.excludes) {
          sinkConfigAuto.mapping.excludes = sinkMappingObj.excludes;
        }

        this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
          sinkConfig: sinkConfigAuto,
        });
      });
    } else if (sinkMappingObj) {
      const _isEditMode = this._isEditMode();
      let sinkConfigWithMapping = {
        ...sinkConfig,
        mapping: UIUtils.buildMapping(
          connectionMeta.connectionCategory,
          sinkConfig.data_format,
          sinkMapping.obj,
          sinkConfig.mapping,
          _isEditMode ? {} : { mode: 'manual' },
        ),
      };
      this.setState(
        {
          sinkMapping,
        },
        () => {
          this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
            sinkConfig: sinkConfigWithMapping,
          });
        },
      );
    } else {
      this.setState({
        sinkMapping,
      });
    }
  }

  _handleFormDismissed() {
    const { previousPath, location, isModal } = this.props;
    const { pathname = '' } = location || {};

    if (isModal) {
      this._redirectAfterCompletion(); // skip the redirect
    } else {
      this._redirectAfterCompletion(
        previousPath ||
          (isNUIURLStartsWith(pathname, ROUTE_DATA_SETS_SHARED_WITH_ME)
            ? ROUTE_DATA_SETS_SHARED_WITH_ME
            : isNUIURLStartsWith(pathname, ROUTE_DESTINATIONS)
              ? ROUTE_DESTINATIONS
              : ROUTE_MY_DATA_SETS),
      );
    }
  }

  _handleFormSubmitted() {
    const { destinationDraft, dataSetIdParam, isModal } = this.props;
    const { isDestinationDraftEdited } = this.state;

    if (Boolean(destinationDraft) && isDestinationDraftEdited) {
      this.setState({ isDestinationDraft: true });
    } else {
      if (isModal) {
        this._redirectAfterCompletion(); // skip the redirect to the destination list
      } else {
        this._redirectAfterCompletion(ROUTE_DESTINATIONS);
      }
    }
  }

  _handleEnterReviewStage() {
    this.props.markFormCompleted(FORM_TYPE_EDIT_DESTINATION);
  }

  _validateSelectConnection(data, onSuccess, onError) {
    if (data.selectedConnection) {
      onSuccess();
    } else {
      onError('Please select a connection type.');
    }
  }

  _validateSelectCredential(data, onSuccess, onError) {
    const { isNewCredential, selectedCredentialInfo, selectedCredentialId } = data;

    // required field validation
    if (isNewCredential) {
      //this is new credential. verify that all required fields are populated
      const { paging, selectedConnection } = this.props;
      const { templates, items } =
        paging.connAuthById[
          (selectedCredentialInfo && selectedCredentialInfo._vendorConnectionType) ||
            selectedConnection
        ] || {};
      const missingFields = getEmptyRequiredFields(
        this.getActiveItems(templates, items, this.state.selectedTemplate),
        selectedCredentialInfo,
      );

      //check if credential name has been added or not.
      if (!get(selectedCredentialInfo, 'cred_name')) {
        missingFields.unshift({
          name: 'cred_name',
          display_name: 'Credential Name',
        });
      }

      const errorMessage = formatValidationErrorMessage(missingFields);

      if (errorMessage) {
        onError(errorMessage);
        return;
      }
    } else if (!data.selectedCredentialId) {
      onError('Please select or create a credential');
      return;
    }

    // create new credential or probe the selected one
    const { credentialCreationStatus, credentialProbeStatus, credentialEntities } = this.props;
    const { selectCredentialAwaitingCreation, selectCredentialAwaitingProbe } = this.state;

    this._boundValidateSelectCredential = this._validateSelectCredential.bind(this, ...arguments);

    // if credential probe was successful, credentials are valid
    if (selectCredentialAwaitingProbe) {
      this.setState({
        selectCredentialAwaitingProbe: false,
      });

      if (!credentialProbeStatus.isValid) {
        onError(credentialProbeStatus.message || 'The selected credentials did not work.');
        return;
      }

      const selectedCredential = credentialEntities[credentialProbeStatus.credential];
      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        selectedCredential,
        selectedCredentialId: selectedCredential.id,
      });
      onSuccess();
      return;
    }

    if (isNewCredential) {
      if (selectCredentialAwaitingCreation) {
        this.setState({
          selectCredentialAwaitingCreation: false,
        });

        // if creation failed, error
        if (credentialCreationStatus.message) {
          onError(credentialCreationStatus.message);
          return;
        }

        const createdCredentialId = credentialCreationStatus.credential;
        const createdCredential = credentialEntities[createdCredentialId];
        const credentialName = createdCredential?.name || 'Credential';

        this.props.showSuccess?.(`${credentialName} created successfully`);

        // if cred created, probe it
        this.setState(
          {
            selectCredentialAwaitingProbe: true,
          },
          () => {
            this._handleRequestCredentialProbe(credentialCreationStatus.credential);
          },
        );
        return;
      }

      // request cred creation
      this.setState(
        {
          selectCredentialAwaitingCreation: true,
        },
        () => {
          let credentialId = credentialCreationStatus ? credentialCreationStatus.credential : null;
          this._handleRequestCredentialCreation(selectedCredentialInfo, credentialId);
        },
      );
      return;
    }

    // probe selected credential
    this.setState(
      {
        selectCredentialAwaitingProbe: true,
      },
      () => {
        this._handleRequestCredentialProbe(selectedCredentialInfo.id || selectedCredentialId, true);
      },
    );
  }

  _validateConfigureNoSQL(data, onSuccess, onError) {
    const {
      selectedBucket,
      formData: { isNewCollection = false },
    } = this.props;

    const advancedMode = data.sinkConfig['ui.mode'] === 'code';

    if (advancedMode) {
      this._validateFormCompletion(data, onSuccess, onError);
      return;
    }

    if (isNewCollection) {
      this._validateFormCompletion(data, onSuccess, onError);
    } else if (isEmpty(selectedBucket)) {
      onError('Please select a collection');
    } else {
      const { collection = '' } = selectedBucket;
      if (!collection) {
        onError('Please select a collection');
      } else {
        this._validateFormCompletion(data, onSuccess, onError);
      }
    }
  }

  _validateFormCompletion(data, onSuccess, onError) {
    const {
      paging,
      dataMap,
      updateDataMap,
      addedDataSink,
      addedDataSinkError,
      updatedDataSink,
      updatedDataSinkError,
      dataSchema,
      selectedConnection,
      selectedBucket,
      selectedCredentialId,
    } = this.props;

    const ignorePath = CONNECTORS_WITHOUT_PATH.includes(selectedConnection);

    const sinkConfigFields = paging.sinkConfigById[selectedConnection];
    let subscriptionField = sinkConfigFields.find((field) => field.name === 'subscription');

    const { awaitingSinkCreation, awaitingSinkUpdate } = this.state;

    const connectionCategory =
      data.selectedConnection && paging.metaInfoById[data.selectedConnection].connectionCategory;

    const sinkConfig = get(data, 'sinkConfig');
    const isAdvancedMode = isUiModeCode(sinkConfig);

    const datasetContainsDestination = (() => {
      if (!data?.selectedConnection) return false;
      if (
        [CONNECTION_CATEGORY_DATA_MAP, CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT].includes(
          connectionCategory,
        )
      )
        return true;
      if (!data?.selectedCredential && !data?.credentialCreationStatus) return false;
      // Removing this check as we are adding support to finish creation even if probe/tree fails

      // if (
      //   !(data.credentialProbeStatus || data.credentialCreationStatus).isValid
      // )
      //   return false;
      return true;
    })();

    if (
      !isAdvancedMode &&
      connectionCategory === CONNECTION_CATEGORY_FILE &&
      !ignorePath &&
      !data.sinkConfig.path
    ) {
      onError('Please select the destination directory');
      return;
    }

    if (!datasetContainsDestination) {
      onError('Please complete destination setup');
      return;
    }

    const datasetDatamapNoName =
      data.selectedConnection &&
      connectionCategory === CONNECTION_CATEGORY_DATA_MAP &&
      (!data.dataMapSettings || (data.dataMapSettings && !data.dataMapSettings.name));
    if (datasetDatamapNoName) {
      onError('Please set a name for this lookup');
      return;
    }

    const datasetDatamapNoPrimaryKey =
      data.selectedConnection &&
      connectionCategory === CONNECTION_CATEGORY_DATA_MAP &&
      (!data.dataMapSettings || (data.dataMapSettings && !data.dataMapSettings.map_primary_key));
    if (datasetDatamapNoPrimaryKey) {
      onError('Please set a primary key for this lookup');
      return;
    }

    const attributeMappingMode = get(data, 'sinkConfig.mapping.mode');

    if (
      data.selectedConnection === CONNECTION_CATEGORY_DATA_MAP &&
      attributeMappingMode !== 'auto' &&
      size(dataSchema) < 2
    ) {
      onError('Please add at least two attributes');
      return;
    }

    if (
      !isAdvancedMode &&
      sinkConfig &&
      sinkConfig['insert.mode'] === 'UPSERT' &&
      !sinkConfig['primary.key']
    ) {
      onError('Please configure a primary key for this destination');
      return;
    }

    let mappedFields = [];
    if (sinkConfig.mapping && sinkConfig.mapping.mapping) {
      mappedFields = map(values(sinkConfig.mapping.mapping), (value) => head(keys(value)));
    }
    let primaryKeys = split(sinkConfig['primary.key'], ',');
    let notMappedKeys = difference(primaryKeys, mappedFields);
    if (
      !isAdvancedMode &&
      sinkConfig &&
      sinkConfig['insert.mode'] === 'UPSERT' &&
      sinkConfig['primary.key'] &&
      notMappedKeys.length > 1
    ) {
      onError('Primary key should be mapped to an attribute for this destination.');
      return;
    }

    if (connectionCategory === CONNECTION_CATEGORY_KAFKA && isAdvancedMode === false) {
      // Mode-specific validation: in create-new mode, the typed name is what
      // gets saved (no fallback to selectedTopic); in pick-existing mode, only
      // selectedTopic matters. This catches the case where the user ticked
      // "Create a New Topic" but never typed a name.
      const missingTopic = data.isNewTopicChecked ? !data.newTopicName : !data.selectedTopic;

      if (missingTopic) {
        onError('Please set a topic/queue to write to.');
        return;
      }
    }

    if (!!subscriptionField && data.isNewTopicChecked && !data.subscription) {
      onError('Please set a Subscription name');
      return;
    }

    // only adding required field check for Database Connection type
    if (!isAdvancedMode && connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      if (isEmpty(selectedBucket)) {
        onError('Please select a table');
      } else {
        const { database } = selectedBucket;
        if (!database) {
          onError('Please select a table');
        }
      }

      const mapping = get(sinkConfig, 'mapping.mapping', {});

      if (
        (data.selectedConnection &&
          get(sinkConfig, 'mapping.mode') !== 'auto' &&
          isEmpty(mapping)) ||
        !!mapping.fields_order
      ) {
        onError('Please choose at least one attribute.');
        return;
      }
    }

    // general required field validation
    if (
      !isAdvancedMode &&
      (connectionCategory === CONNECTION_CATEGORY_DATABASE ||
        connectionCategory === CONNECTION_CATEGORY_VECTOR_DB ||
        (CONNECTORS_WITHOUT_PATH.includes(selectedConnection) &&
          connectionCategory === CONNECTION_CATEGORY_FILE))
    ) {
      //TODO: NEX-15904: this is a temporary fix to allow the creation of a sink without a path for file connectors. We need to refactor this to be more generic.
      const missingFields = getEmptyRequiredFields(sinkConfigFields, sinkConfig);

      const errorMessage = formatValidationErrorMessage(missingFields);

      if (errorMessage) {
        onError(errorMessage);
        return;
      }
    }

    if (
      has(sinkConfig, 'batch.mode') &&
      !!sinkConfig['batch.mode'] &&
      has(sinkConfig, 'ui.batch_algo') &&
      sinkConfig['ui.batch_algo'] === Constants.GROUPING_ALGO[1].value && // Grouping algo ALGO_JSON check
      has(sinkConfig, 'ui.body.transform.function') &&
      !!sinkConfig['ui.body.transform.function'] &&
      !JSONUtils.isJSON(sinkConfig['ui.body.transform.function'])
    ) {
      onError('Please enter a valid JSON');
      return;
    }

    if (
      !isAdvancedMode &&
      connectionCategory === CONNECTION_CATEGORY_REST &&
      isEmpty(get(sinkConfig, 'url.template'))
    ) {
      onError('Please enter an endpoint URL.');
      return;
    }

    if (!isAdvancedMode && data.isNewTable) {
      const { database, table } = sinkConfig;
      const { tableNamesByDatabase, selectedTable, selectedConnection } = data;

      if (isEmpty(table)) {
        onError('Please assign a name to the table.');
        return;
      }

      if (
        database &&
        tableNamesByDatabase[database] &&
        tableNamesByDatabase[database].indexOf(table) > -1
      ) {
        onError(`A table named "${table}" already exists.`);
        return;
      }

      if (!isValidDBColumnName(table, selectedConnection)) {
        onError(`Invalid table name: ${table}`);
        return;
      }

      const columns = get(selectedTable, 'columns', []);

      for (let i = columns.length - 1; i > -1; i--) {
        const columnName = columns[i].name;

        if (!isValidDBColumnName(columnName, selectedConnection)) {
          onError(`Invalid column name: ${columnName}`);
          return;
        }
      }
    }

    // NoSQL Validation
    if (
      !isAdvancedMode &&
      connectionCategory === CONNECTION_CATEGORY_NOSQL &&
      data.isNewCollection
    ) {
      const { database, collection } = sinkConfig;
      const { collectionNamesByDatabase } = data;

      if (isEmpty(collection)) {
        onError('Please assign a name to the collection.');
        return;
      }

      if (
        database &&
        collectionNamesByDatabase[database] &&
        collectionNamesByDatabase[database].indexOf(collection) > -1
      ) {
        onError(`A collection named "${collection}" already exists.`);
        return;
      }
    }

    // if schema update was successful, validation complete
    if (awaitingSinkCreation) {
      if (addedDataSinkError) {
        this.setState({
          awaitingSinkCreation: false,
        });

        onError(addedDataSinkError.message);
        return;
      }

      if (addedDataSink) {
        this.setState({
          awaitingSinkCreation: false,
        });

        onSuccess();
        return;
      }
    }

    if (awaitingSinkUpdate) {
      if (updatedDataSinkError) {
        this.setState({
          awaitingSinkUpdate: false,
        });

        onError(updatedDataSinkError.message);
        return;
      }

      if (updatedDataSink) {
        this.setState({
          awaitingSinkUpdate: false,
        });

        onSuccess();
        return;
      }
    }

    //Trigger DataSub creation and wait for new sub to be created
    this._boundValidateFormCompletion = this._validateFormCompletion.bind(this, ...arguments);

    if (!awaitingSinkCreation && !awaitingSinkUpdate) {
      //if sink is a data map and attribute map changed, a) validate the primary key is still present, and b) update secondary key defaults
      if (
        addedDataSink &&
        paging.metaInfoById[addedDataSink.sink_type] &&
        paging.metaInfoById[addedDataSink.sink_type].connectionCategory ===
          CONNECTION_CATEGORY_DATA_MAP &&
        dataMap
      ) {
        const { data_defaults } = dataMap;
        const { map_primary_key } = data.dataMapSettings;
        const dataDefaultKeys = keys(data_defaults);
        const dataSchemaAttrs = map(dataSchema, '__name');
        if (!map_primary_key || indexOf(dataSchemaAttrs, map_primary_key) === -1) {
          onError('Please set a primary key for the destination data map');
          return;
        }
        const droppedAttrs = difference(dataDefaultKeys, dataSchemaAttrs);
        if (droppedAttrs.length > 0) {
          // some attributes were dropped. we should update the data map defaults
          const newDataDefaults = { ...data_defaults };
          forEach(droppedAttrs, (attr) => {
            delete newDataDefaults[attr];
          });
          updateDataMap(dataMap.id, { data_defaults: newDataDefaults });
        }
      }

      this.setState(
        addedDataSink ? { awaitingSinkUpdate: true } : { awaitingSinkCreation: true },
        () => {
          this._requestSaveSink(
            data.sinkConfig,
            data.selectedCredential,
            data.dataMapSettings,
            data.isNewTable,
          );
        },
      );
    }
  }

  _requestSaveSink(sinkConfig, selectedCredential, dataMapSettings, isNewTable) {
    const { addedDataSink } = this.props;
    let selectedCredentialId = selectedCredential ? selectedCredential.id : null;

    if (addedDataSink) {
      let { name, description, ...sink_config } = sinkConfig;
      // sink_type lives at the root of the sink entity, not inside sink_config. For
      // connectors like REST that no longer store it in sink_config (NEX-21633), fall
      // back to selectedConnection so the PUT-vs-POST comparison below stays correct.
      let { sink_type = this.props.selectedConnection } = sink_config;
      const nameSink = name || addedDataSink?.name || this._getSinkName();
      const descSink = description || addedDataSink?.description || '';

      // Strip ui.mode: "view" before sending to API — only "code" should be persisted
      if (sink_config['ui.mode'] === UiMode.VIEW) {
        delete sink_config['ui.mode'];
      }

      let sinkInfo = {
        name: nameSink,
        description: descSink,
        sink_type,
        sink_config,
        data_credentials_id: selectedCredentialId,
      };

      let meta = this.props.paging.metaInfoById[sinkInfo.sink_type];
      let connectionCategory = meta && meta.connectionCategory;

      if (connectionCategory === CONNECTION_CATEGORY_DATA_MAP) {
        let { id, name, description, map_primary_key, ...dataDefaults } = dataMapSettings;
        let data_defaults = mapKeys(dataDefaults, (v, k) => {
          return replace(k, 'data_defaults.', '');
        });

        let dataMapInfo = {
          id,
          name,
          description,
          map_primary_key,
          emit_data_default: true,
          public: false,
          data_defaults,
        };
        sinkInfo.name = dataMapInfo.name;
        sinkInfo.data_map = dataMapInfo;
      }

      // For Kafka
      if (connectionCategory === CONNECTION_CATEGORY_KAFKA) {
        sinkInfo.create_destination = get(sinkConfig, 'ui.is_new_topic', false) ? 'true' : 'false';
      }
      // For Database connection category
      if (isNewTable) {
        sinkInfo.create_destination = 'true';
      }

      const savedSinkConnectionType =
        sinkConfig.sink_type === 'rest' && sinkConfig.vendor_id
          ? sinkConfig.sink_type
          : getConnectionOrVendorType(
              this.props.addedDataSink,
              this.props.vendors,
              'sink_type',
              'vendor_id',
            );

      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        updatedDataSinkError: null,
        updatedDatasetError: null,
      });

      if (savedSinkConnectionType !== sinkInfo.sink_type) {
        sinkInfo.data_set_id = this.props.addedDataSink.data_set_id;
        this.props.updateSinkTypeFromForm(
          FORM_TYPE_EDIT_DESTINATION,
          sinkInfo.sink_type,
          sinkInfo,
          selectedCredentialId,
        );
      } else {
        this.props.updateSinkInfoFromForm(
          FORM_TYPE_EDIT_DESTINATION,
          this.props.addedDataSink.id,
          sinkInfo,
        );
      }
    } else {
      let { name, description, ...sink_config } = sinkConfig;
      // Same fallback as the edit path above — see NEX-21633.
      let { sink_type = this.props.selectedConnection } = sink_config;

      let sinkInfo = {
        name: name || this._getSinkName(),
        description,
        sink_type,
        sink_config,
      };

      let meta = this.props.paging.metaInfoById[sinkInfo.sink_type];
      let connectionCategory = meta && meta.connectionCategory;
      if (connectionCategory === CONNECTION_CATEGORY_DATA_MAP) {
        let { id, name, description, map_primary_key, ...dataDefaults } = dataMapSettings;
        let data_defaults = mapKeys(dataDefaults, (v, k) => {
          return replace(k, 'data_defaults.', '');
        });

        let dataMapInfo = {
          id,
          name,
          description,
          map_primary_key,
          emit_data_default: true,
          public: false,
          data_defaults,
        };
        sinkInfo.name = dataMapInfo.name;
        sinkInfo.data_map = dataMapInfo;
      }

      // For Kafka
      if (connectionCategory === CONNECTION_CATEGORY_KAFKA) {
        sinkInfo.create_destination = get(sinkConfig, 'ui.is_new_topic', false) ? 'true' : 'false';
      }
      // For Database connection category
      if (isNewTable) {
        sinkInfo.create_destination = 'true';
      }

      sinkInfo.data_set_id = this.props.formData.dataSetId;
      this.props.updateFormData(FORM_TYPE_EDIT_DESTINATION, {
        addedDataSinkError: null,
      });

      this.props.updateSinkTypeFromForm(
        FORM_TYPE_EDIT_DESTINATION,
        sinkInfo.sink_type,
        sinkInfo,
        selectedCredentialId,
      );
    }
  }

  _redirectAfterCompletion(listingPath) {
    const { dataSet, previousPath, refreshPipeline, isModal, onDismiss } = this.props;
    const pipelineFormPath = this._getPipelineFormPath();
    let redirectPath;

    if (!listingPath && isModal && onDismiss) {
      onDismiss(this.props.formData);
      return;
    }

    if (pipelineFormPath) {
      redirectPath = pipelineFormPath;
    } else if (previousPath) {
      redirectPath = previousPath;
    } else {
      redirectPath = listingPath;
    }

    if (this.state.isDestinationDraft) {
      redirectPath = listingPath;
    }

    this.setState({ redirectPath });
  }

  _getSinkName() {
    let {
      paging: { metaInfoById, sinkConfigById },
      formData,
    } = this.props;
    const { selectedConnection, selectedCredential, dataMapSettings, sinkConfig } = formData;
    const meta = metaInfoById[selectedConnection];
    const connectionCategory = meta && meta.connectionCategory;
    let sinkName;

    if (
      connectionCategory === CONNECTION_CATEGORY_REST ||
      (connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API &&
        sinkConfig.sink_type === 'rest' &&
        sinkConfig.vendor_id)
    ) {
      const { hostname, lastPath } = UIUtils.parsePartsFromURL(sinkConfig['url.template']);
      sinkName =
        lastPath && lastPath !== ''
          ? `${capitalize(hostname)} - ${lastPath}`
          : `${capitalize(hostname)}`;
    } else if (connectionCategory === CONNECTION_CATEGORY_SOAP) {
      sinkName = `${sinkConfig['soap.service']} - ${sinkConfig['soap.operation']}`;
    } else if (
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API ||
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT
    ) {
      const endpointId = sinkConfig.vendor_endpoint_id;
      const configFields = sinkConfigById[selectedConnection];
      const endpointField = find(configFields, {
        name: 'vendor_endpoint_id',
      });
      const endpointOption = find(endpointField?.options, {
        value: endpointId,
      });
      sinkName = endpointOption
        ? endpointOption.label
        : endpointId?.toString(10) || meta?.displayName || selectedConnection;
    } else if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      const { selectedTable } = formData;
      const database =
        selectedTable && has(selectedTable, 'database') ? selectedTable.database : null;
      const name = selectedTable && has(selectedTable, 'name') ? selectedTable.name : null;

      const id = selectedTable && has(selectedTable, 'id') ? selectedTable.id : null;

      if (database && name) {
        sinkName = `${database} - ${name}`;
      } else if (id) {
        sinkName = `${id}`;
      } else if (database) {
        sinkName = `${database}`;
      } else {
        sinkName = `${selectedCredential && selectedCredential.name} - Query`;
      }
    } else if (connectionCategory === CONNECTION_CATEGORY_KAFKA) {
      // Determine the correct topic field name based on connector type
      const sinkConfigFields = sinkConfigById[selectedConnection];
      let topicField = sinkConfigFields.find(
        (field) => field.name === 'target.name' || field.name === 'topic',
      );
      const topicFieldName = topicField ? topicField.name : 'topic';
      sinkName = sinkConfig[topicFieldName] || `${selectedConnection} - Topic`;
    } else if (connectionCategory === CONNECTION_CATEGORY_FILE) {
      const { selectedBucket } = formData;
      const ignorePath = CONNECTORS_WITHOUT_PATH.includes(this.props.selectedConnection);
      let leafNodeName;

      if (selectedBucket && (selectedBucket.displayName || selectedBucket.name)) {
        leafNodeName = selectedBucket.displayName || selectedBucket.name;
      } else if (selectedBucket && (selectedBucket.displayPath || selectedBucket.path)) {
        const path = selectedBucket.displayPath || selectedBucket.path;
        leafNodeName = path.substr(path.lastIndexOf('/') + 1);
      } else {
        leafNodeName = this.props.selectedConnection + ' - ' + this.props.selectedCredentialId;
      }

      //TODO: ftp etc source types have bucket name /. Need backend to change the types maybe?
      sinkName = leafNodeName;
    } else {
      const name =
        (dataMapSettings && dataMapSettings.name) ||
        (selectedCredential && selectedCredential.name);
      sinkName =
        !name || !selectedConnection
          ? null
          : `${metaInfoById[selectedConnection].displayName} - ${name}`;
    }

    return sinkName;
  }

  _getPipelineFormPath() {
    const { previousPath } = this.props;

    if (previousPath && PIPELINE_FORM_PATH_PATTERN.test(previousPath)) {
      return previousPath;
    }

    return null;
  }

  loadVendorInfo = (connection) => {
    const { loadFullVendor } = this.props;
    if (connection) {
      return loadFullVendor(connection);
    }
  };

  onChangeApiType = (connector) => {
    const isEditMode = this._isEditMode();
    this.loadVendorInfo(connector)?.then(() => {
      if (isEditMode) {
        this._initializeEditForm();
      } else {
        this._initializeCreateForm();
      }
    });
  };

  onConnectionSelected = (nextStageIndex) => {
    const { selectedConnection } = this.props;
    const enterAfterSelection = nextStageIndex === 1;
    if (selectedConnection && enterAfterSelection) {
      const promise = this.loadVendorInfo(selectedConnection);
      if (promise) {
        promise
          .then(() => {
            this._initializeCreateForm(selectedConnection);
            this.updateSinkConfig(selectedConnection);
          })
          .catch(() => {
            this.updateSinkConfig(selectedConnection);
            this._initializeCreateForm(selectedConnection);
          });
      }
      AnalyticsUtils.captureCustomEvent('data_sink_connection_selected', {
        selected_connector: selectedConnection,
      });
    }
  };

  handleToggleDestinationDraft = (destinationDraftState = false) => {
    this.setState({ isDestinationDraftEdited: destinationDraftState });
  };

  goBack = () => {
    if (this.props.isModal) {
      this.props.onDismiss?.();
      return;
    }
    if (this.props.history) {
      this.props.history.goBack();
    }
  };

  onError = (msg) => this.props.setFormError(FORM_TYPE_EDIT_DESTINATION, msg);

  render() {
    if (this.state.redirectPath) {
      return <Redirect to={this.state.redirectPath} />;
    }

    const {
      paging,
      selectedConnection,
      credentials,
      vendors,
      currentOrg,
      addedDataSink,
      dataSet,
      selectedCredential,
      selectedCredentialId,
      dataMapSettings,
      selectedBucket,
      scriptCredentialsData,
      allCredentials,
      currentStage,
      formData,
      sinkConfig,
      dataSchema,
      probeData,
      probeDataFlat,
      probeError,
      probeTreeLastUpdatedAt,
      isLoadingProbeData,
      isLoadingCredentials,
      isVerifyingCredential,
      credentialsProbingId,
      orderedOutputSchema,
      excludeByAttrName,
      updateFormData,
      isModal,
    } = this.props;

    if (sinkConfig?.['cdc.enabled']) {
      return (
        <Redirect
          to={`${this.context}${ROUTE_PIPELINES}/cdc/sink/${this.props.sinkIdParam}/edit`}
        />
      );
    }

    const isEditMode = this._isEditMode();
    const sinkConfigFields = selectedConnection ? paging.sinkConfigById[selectedConnection] : null;
    const { templates } = selectedConnection
      ? paging.connAuthById[
          (formData.selectedCredentialInfo &&
            formData.selectedCredentialInfo._vendorConnectionType) ||
            selectedConnection
        ]
      : {};
    const connectionAuthMeta = selectedConnection
      ? paging.connAuthMeta[
          (formData.selectedCredentialInfo &&
            formData.selectedCredentialInfo._vendorConnectionType) ||
            selectedConnection
        ]
      : null;
    const metaSelectedConnection = paging.metaInfoById[selectedConnection] || {};
    const sinkConfigFieldsForFile = filter(
      sinkConfigFields,
      (f) =>
        f.name !== 'data_format' &&
        f.name !== 'file.name.prefix' &&
        f.name !== 'orc.compress' &&
        f.name !== 'max.file.size.mb' &&
        f.name !== 'xml.root' &&
        f.name !== 'write.header' &&
        f.name !== 'field.lengths' &&
        f.name !== 'padding.character' &&
        f.name !== 'line.separator' &&
        f.name !== 'range' &&
        f.name !== 'insert.mode' &&
        f.name !== 'clear.range' &&
        !Constants.HC_SINK_FIELD_NAMES.includes(f.name),
    );

    const allowCustomPrefixEditor =
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_FILE &&
      FileUtil.isAllowedToUseCustomPrefixEditor(selectedBucket, sinkConfig);

    const pipelineFormPath = this._getPipelineFormPath();
    const showBackButton = this._isEditMode()
      ? currentStage > 1 // In edit mode, don't allow going back to stage 0
      : currentStage !== this._getInitialStageIndex(metaSelectedConnection.connectionCategory);

    const isNexsetDetected = isDetectedDataset(dataSet);

    const formStages = [
      <FormPage key="Select" title="Select" validationMethod={this._validateSelectConnection}>
        <SelectConnection
          id={0}
          connectorResourceType={CONNECTORS_RESOURCE_TYPES.DESTINATION}
          connectionMetas={paging.metaInfoById}
          connectionTypes={paging.sinksSupported}
          connectionsAllowed={paging.connectionsByFlowType?.streaming?.sink || paging.sinksAllowed}
          connectionsComingSoon={paging.soonForFlowTypes?.streaming?.sink}
          selectedConnection={selectedConnection}
          onSelected={this._handleConnectionSelected}
          setContextualSupport={this.props.setContextualSupport}
          unsetContextualSupport={this.props.unsetContextualSupport}
          connectionCategories={
            isCapsuleCluster(dataSet) ? CAPSULE_FLOW_DESTINATION_CATEGORIES : []
          }
          credentialsByType={credentials}
        />
      </FormPage>,
    ];

    if (metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_DATA_MAP) {
      formStages.push(
        <FormPage
          key="Configure"
          title="Configure"
          nextButtonText="Save"
          showBackButton={showBackButton}
          validationMethod={this._validateFormCompletion}
        >
          <EditDynamicDataMap
            sinkConfigFields={sinkConfigFields || []}
            dataMapSettings={dataMapSettings}
            formType={FORM_TYPE_EDIT_DESTINATION}
            updateFormData={updateFormData}
            sinkConfig={sinkConfig}
            selectedConnection={selectedConnection}
            dataSchema={dataSchema}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
          />
        </FormPage>,
      );
    } else if (
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_FILE ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_REST ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_DATABASE ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_VECTOR_DB ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_NOSQL ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_KAFKA ||
      !metaSelectedConnection.connectionCategory
    ) {
      formStages.push(
        <FormPage
          key="Authenticate"
          title="Authenticate"
          showBackButton={showBackButton}
          validationMethod={this._validateSelectCredential}
        >
          <AuthenticateDataSource
            onChangeApiType={this.onChangeApiType}
            connectionTypes={paging.sinksSupported}
            connectionMetas={paging.metaInfoById}
            vendors={vendors}
            selectedConnection={selectedConnection}
            selectedTemplate={this.state.selectedTemplate}
            onChangeTemplate={this.onChangeTemplate}
            templates={templates}
            connectionAuthFields={this.state.connectionAuthFields}
            connectionAuthMeta={connectionAuthMeta}
            dataCredentials={credentials}
            credentialsById={allCredentials}
            isFormAwaitingValidation={isVerifyingCredential}
            isLoadingData={isLoadingCredentials}
            formData={formData}
            onCredSelectionChanged={this._handleCredSelectionChanged}
            onFormInputChanged={this._handleCredentialFormInputChanged}
            onRefreshClicked={this._loadCredentials}
            onNewDropboxCredential={this._handleNewDropboxCredential}
            onNewBoxCredential={this._handleNewBoxCredential}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
          />
        </FormPage>,
      );
    }

    if (
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_FILE ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_VECTOR_DB ||
      !metaSelectedConnection.connectionCategory
    ) {
      formStages.push(
        <FormPage
          key="Configure"
          title="Configure"
          nextButtonText={'Save'}
          showBackButton={showBackButton}
          validationMethod={this._validateFormCompletion}
        >
          <SelectBucket
            sink={this.props.sink}
            dataSet={this.props.dataSet}
            sourceName={this.props.sink?.name}
            sourceDescription={this.props.sink?.description}
            allowCustomPrefixEditor={allowCustomPrefixEditor}
            selectedCredentialId={selectedCredentialId}
            selectedConnection={selectedConnection}
            connectionMetas={paging.metaInfoById}
            sourceConfigFields={sinkConfigFieldsForFile}
            sourceConfig={sinkConfig}
            enableFileSelection={true}
            selectableFileMimeType="application/vnd.google-apps.spreadsheet"
            onSourceConfigChanged={this._handleSinkConfigChanged}
            validationInfo={this.state.validationInfo}
            onValidationComplete={() => {}}
            onSelected={this._handleProbeBucketSelected}
            onRequestProbeTree={this._onRequestProbeTree}
            onToggleNodeProbeTree={this._onToggleNodeProbeTree}
            probeData={probeData}
            probeError={probeError}
            probeTreeLastUpdatedAt={probeTreeLastUpdatedAt}
            isLoadingProbeData={isLoadingProbeData}
            onRequestSourceCreation={() => {}}
            selectedBucket={selectedBucket}
            dataSchema={dataSchema}
            resourceMode="sink"
            paging={paging}
            formType={FORM_TYPE_EDIT_DESTINATION}
            selectedDestinationType={selectedConnection}
            sinkConfigFields={paging.sinkConfigById[selectedConnection]}
            addedDataSink={addedDataSink}
            dataSetId={dataSet && dataSet.id}
            sinkConfig={sinkConfig}
            onSinkConfigChanged={this._handleSinkConfigChanged}
            onSaveAdvancedMode={this.onSaveAdvancedMode}
            orderedDataSchema={orderedOutputSchema}
            excludeByAttrName={excludeByAttrName}
            onToggleExclude={this._onToggleExcludeAttr}
            updateOrderedDataSchema={this._onUpdateOrderedDataSchema}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
            connectionCategory={CONNECTION_CATEGORY_FILE}
            onInputChange={this.onInputChange}
            goBack={this.goBack}
            onError={this.onError}
          />
        </FormPage>,
      );
    } else if (metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_REST) {
      formStages.push(
        <FormPage
          key="Configure"
          title="Configure"
          nextButtonText="Save"
          showBackButton={showBackButton}
          validationMethod={this._validateFormCompletion}
        >
          <RestConfigure
            dataSet={dataSet}
            isEditMode={isEditMode}
            formType={FORM_TYPE_EDIT_DESTINATION}
            selectedConnection={selectedConnection}
            addedDataSink={addedDataSink}
            dataSchema={dataSchema}
            onSinkConfigChanged={this._handleSinkConfigChanged}
            onSinkMappingUpdated={this._handleSinkMappingUpdated}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
            parentDataSet={this.props.parentDataSet}
            parentDataSetSamples={this.props.parentDataSetSamples}
            redirectAfterCompletion={this._redirectAfterCompletion}
            onToggleDestinationDraft={this.handleToggleDestinationDraft}
            onSaveAdvancedMode={this.onSaveAdvancedMode}
            onError={this.onError}
            goBack={this.goBack}
          />
        </FormPage>,
      );
    } else if (metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_KAFKA) {
      formStages.push(
        <FormPage
          key="Configure"
          title="Configure"
          nextButtonText="Save"
          showBackButton={showBackButton}
          validationMethod={this._validateFormCompletion}
        >
          <ConfigureKafkaSinkContainer
            formType={FORM_TYPE_EDIT_DESTINATION}
            dataSet={dataSet}
            dataSchema={dataSchema}
            selectedConnection={selectedConnection}
            selectedCredentialId={selectedCredentialId}
            sinkConfigFields={sinkConfigFields || []}
            addedDataSink={addedDataSink}
            sinkConfig={sinkConfig}
            goBack={this.goBack}
            onError={this.onError}
            onSaveAdvancedMode={this.onSaveAdvancedMode}
          />
        </FormPage>,
      );
    } else if (
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API ||
      metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_SCRIPT
    ) {
      formStages.push(
        <FormPage
          key="Configure"
          title="Configure"
          nextButtonText="Save"
          showBackButton={showBackButton}
          validationMethod={this._validateFormCompletion}
        >
          <ConfigureTemplatizedAPISinkContainer
            onChangeApiType={this.onChangeApiType}
            formType={FORM_TYPE_EDIT_DESTINATION}
            connectionAuthFields={this.state.connectionAuthFields}
            dataSet={dataSet}
            sinkConfig={sinkConfig}
            addedDataSink={addedDataSink}
            selectedConnection={selectedConnection}
            selectedCredentialId={selectedCredentialId}
            selectedCredential={selectedCredential}
            sinkConfigById={paging.sinkConfigById}
            dataSchema={dataSchema}
            credentialsData={scriptCredentialsData}
            credentialsByType={credentials}
            credentialsById={allCredentials}
            isLoadingCredentials={isLoadingCredentials}
            onLoadCredentials={this._loadCredentials}
            onScriptCredentialInputChanged={this._handleScriptCredentialInputChanged}
            onValidateNewScriptCredential={this._handleSaveNewScriptCredential}
            onSinkConfigChanged={this._handleSinkConfigChanged}
            onSinkMappingUpdated={this._handleSinkMappingUpdated}
            onTemplatizedAPIModeChanged={this._handleTemplatizedAPIModeChanged}
            onTemplatizedAPIConfigChanged={this._handleTemplatizedAPIConfigChanged}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
            onToggleDestinationDraft={this.handleToggleDestinationDraft}
            goBack={this.goBack}
            onSaveAdvancedMode={this.onSaveAdvancedMode}
            onError={this.onError}
            parentDataSet={this.props.parentDataSet}
            parentDataSetSamples={this.props.parentDataSetSamples}
          />
        </FormPage>,
      );
    } else if (metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_DATABASE) {
      formStages.push(
        <FormPage
          key="Configure"
          title="Configure"
          showBackButton={showBackButton}
          validationMethod={this._validateFormCompletion}
        >
          <DatabaseConfigurator
            goBack={this.goBack}
            addedDataSinkId={this.props.formData?.addedDataSinkId}
            dataSet={this.props.dataSet}
            addedDataSink={addedDataSink}
            nextButtonText="Save"
            currentOrg={currentOrg}
            dataSchema={this._getSinkMappingAttributes(
              selectedConnection,
              this.props.dataSchema,
              sinkConfig,
            )}
            type="configure"
            isEditMode={isEditMode}
            sinkConfigFields={paging.sinkConfigById}
            sinkConfig={sinkConfig}
            onSinkConfigChanged={this._handleSinkConfigChanged}
            selectedDatabase={(selectedBucket || {}).database}
            selectedTable={this.props.selectedTable}
            formType={FORM_TYPE_EDIT_DESTINATION}
            onTableSelected={this._handleTableSelected}
            updateFormData={this.props.updateFormData}
            selectedCredentialId={selectedCredentialId}
            onRequestProbeTree={this._onRequestProbeTree}
            onRequestInitialDBProbeTree={this._onRequestInitialDBProbeTree}
            isLoadingProbeData={isLoadingProbeData}
            connectionMetas={paging.metaInfoById}
            selectedConnection={selectedConnection}
            probeData={probeData}
            probeDataFlat={probeDataFlat}
            probeError={probeError}
            isNewTable={formData.isNewTable}
            onSinkMappingUpdated={this._handleSinkMappingUpdated}
            loadDBDataTypes={this.props.loadDBDataTypes}
            dbDataTypes={this.props.dbDataTypes}
            schemaToDBDataTypes={this.props.schemaToDBDataTypes}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
            onSaveAdvancedMode={this.onSaveAdvancedMode}
            onError={this.onError}
          />
        </FormPage>,
      );
    } else if (metaSelectedConnection.connectionCategory === CONNECTION_CATEGORY_NOSQL) {
      formStages.push(
        <FormPage
          key="Configure"
          title="Configure"
          nextButtonText="Save"
          showBackButton={showBackButton}
          validationMethod={this._validateConfigureNoSQL}
        >
          <NoSQLConfigurator
            goBack={this.goBack}
            addedDataSink={addedDataSink}
            type="format"
            isEditMode={isEditMode}
            sinkConfigFields={paging.sinkConfigById}
            sinkConfig={sinkConfig}
            onSinkConfigChanged={this._handleSinkConfigChanged}
            selectedDatabase={(selectedBucket || {}).database}
            selectedCollection={this.props.selectedCollection}
            formType={FORM_TYPE_EDIT_DESTINATION}
            onCollectionSelected={this._handleCollectionSelected}
            updateFormData={this.props.updateFormData}
            selectedCredentialId={selectedCredentialId}
            onRequestProbeTree={this._onRequestProbeTree}
            onRequestInitialDBProbeTree={this._onRequestInitialDBProbeTree}
            isLoadingProbeData={isLoadingProbeData}
            connectionMetas={paging.metaInfoById}
            selectedConnection={selectedConnection}
            probeData={probeData}
            probeDataFlat={probeDataFlat}
            probeError={probeError}
            isNewCollection={formData.isNewCollection}
            onSinkMappingUpdated={this._handleSinkMappingUpdated}
            loadDBDataTypes={this.props.loadDBDataTypes}
            dbDataTypes={this.props.dbDataTypes}
            schemaToDBDataTypes={this.props.schemaToDBDataTypes}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
            dataSetId={dataSet && dataSet.id}
            dataSet={dataSet}
            selectedBucket={selectedBucket}
            dataSchema={this._getSinkMappingAttributes(selectedConnection, dataSchema, sinkConfig)}
            orderedDataSchema={orderedOutputSchema}
            excludeByAttrName={excludeByAttrName}
            onToggleExclude={this._onToggleExcludeAttr}
            updateOrderedDataSchema={this._onUpdateOrderedDataSchema}
            onSaveAdvancedMode={this.onSaveAdvancedMode}
            onError={this.onError}
          />
        </FormPage>,
      );
    }

    const authenticateStageIndex = formStages.findIndex(
      (stage) => stage && stage.key === 'Authenticate',
    );
    const blockContinueForCredentialProbe =
      authenticateStageIndex >= 0 &&
      currentStage === authenticateStageIndex &&
      selectedCredentialId != null &&
      credentialsProbingId != null &&
      Number(credentialsProbingId) === Number(selectedCredentialId);

    const containerClassName = cx({ 'resource-edit-modal': isModal });

    return (
      <div id="destination-setup" className={containerClassName}>
        <ConnectionLoader
          loading={this.state.isLoadingInitialData}
          message="Loading Destination..."
        />

        {!this.state.isDestinationDraft && (
          <Form
            isModal={isModal}
            title={getDestinationTitle(isEditMode)}
            type={FORM_TYPE_EDIT_DESTINATION}
            onSubmit={this._handleFormSubmitted}
            onDismiss={this._handleFormDismissed}
            submitButtonText={pipelineFormPath ? 'Save' : 'Done'}
            alwaysShowCancel={true}
            isLoading={this.props.isCreatingSink || this.props.isUpdatingSink}
            blockContinueForCredentialProbe={blockContinueForCredentialProbe}
            browserBackLeavesEditor
          >
            {formStages.map((stage) =>
              React.cloneElement(stage, {
                onEnter: this.onConnectionSelected,
              }),
            )}
          </Form>
        )}
        {this.state.isDestinationDraft && (
          <Form isModal={isModal} title="Send Nexset to Destination" shouldRenderNextButton={false}>
            <NavigatorForEditedNexset
              redirectAfterCompletion={this._redirectAfterCompletion}
              isNexsetDetected={isNexsetDetected}
              dataSetId={dataSet?.id}
            />
          </Form>
        )}
      </div>
    );
  }
}

function getDestinationTitle(isEditMode = false) {
  return (
    <>
      {'Flex Flow '}
      <span style={{ color: 'var(--color-greyscale-200)' }}>/</span>{' '}
      {isEditMode ? `Editing Data Destination` : `New Data Destination`}
    </>
  );
}

export default withSnackbar(DestinationSetup);
